import axios from 'axios';

const MS_SERVICE_PREFIX = '/customer-ui-ms';
const AUTH_SERVICE_PREFIX = '/customer-ui-auth';

const normalizeGatewayOrigin = (gatewayUrl) => {
  if (!gatewayUrl) {
    return gatewayUrl;
  }

  return gatewayUrl
    .trim()
    .replace(/^["']|["']$/g, '')
    .replace(/\/(customer-ui-ms|customer-ui-auth)\/?$/, '')
    .replace(/\/$/, '');
};

const getGatewayOrigin = () => {
  const envGatewayOrigin = normalizeGatewayOrigin(process.env.REACT_APP_API_GATEWAY_URL);
  if (envGatewayOrigin) {
    return envGatewayOrigin;
  }

  if (typeof window !== 'undefined') {
    return window.location.origin;
  }

  return '';
};

//IMPORTANT:  please make sure isBackendLocal is set to false when pushing the code to github

const isBackendLocal = false;
const backendLocalUrl = 'http://localhost:8080';

const GATEWAY_ORIGIN = isBackendLocal ? backendLocalUrl : getGatewayOrigin();

const MS_SERVICE_BASE_URL = isBackendLocal ? GATEWAY_ORIGIN : `${GATEWAY_ORIGIN}${MS_SERVICE_PREFIX}`;
const AUTH_SERVICE_BASE_URL = `${GATEWAY_ORIGIN}${AUTH_SERVICE_PREFIX}`;

// API Configuration
const API_CONFIG = {
  BASE_URL: MS_SERVICE_BASE_URL,
  AUTH_BASE_URL: AUTH_SERVICE_BASE_URL,
  AUTH_TOKEN_KEY: 'access_token',
  STAFF_INFO_KEY: 'staff_info',
  TOKEN_EXPIRY_KEY: 'token_expires_at'
};

// Export the base URL for use in other components (e.g., opening docs in new tab)
export const getApiBaseUrl = () => API_CONFIG.BASE_URL;

const createGatewayClient = ({ baseURL, includeApiKey = false }) => {
  const client = axios.create({
    baseURL,
    withCredentials: false,
    headers: {
      'Content-Type': 'application/json',
      'Accept': 'application/json',
      'Cache-Control': 'no-cache, no-store, must-revalidate',
      'Pragma': 'no-cache',
      'Expires': '0'
    }
  });

  client.interceptors.request.use(
    (config) => {
      const token = sessionStorage.getItem(API_CONFIG.AUTH_TOKEN_KEY);
      if (token) {
        config.headers.Authorization = `Bearer ${token}`;
      }

      let hasApiKey = false;
      if (includeApiKey) {
        const apiKey = sessionStorage.getItem("api_key");
        if (apiKey) {
          config.headers['x-apikey'] = apiKey;
          hasApiKey = true;
        }
      }

      // Log the request details
      console.log(`🌐 requestDebugger API Request: ${config.method?.toUpperCase()} ${config.baseURL}${config.url}`);
      console.log(`📡 requestDebugger Full URL: ${config.baseURL}${config.url}`);
      console.log(`🔧 requestDebugger Config:`, config);
      console.log(`🔑 requestDebugger API Key present:`, hasApiKey);

      return config;
    },
    (error) => {
      console.error('❌ Request interceptor error:', error);
      return Promise.reject(error);
    }
  );

  client.interceptors.response.use(
    (response) => response,
    async (error) => {
      if (error.response?.status === 401) {
        // Clear session storage on unauthorized
        sessionStorage.removeItem(API_CONFIG.AUTH_TOKEN_KEY);
        sessionStorage.removeItem(API_CONFIG.STAFF_INFO_KEY);
        sessionStorage.removeItem(API_CONFIG.TOKEN_EXPIRY_KEY);
        // Redirect to login or handle unauthorized
        window.location.href = '/';
      }
      return Promise.reject(error);
    }
  );

  return client;
};

const apiClient = createGatewayClient({
  baseURL: API_CONFIG.BASE_URL,
  includeApiKey: true
});

const authApiClient = createGatewayClient({
  baseURL: API_CONFIG.AUTH_BASE_URL,
  includeApiKey: false
});

/**
 * Authenticates a staff member and returns their access token and information
 * @param {Object} credentials - The login credentials
 * @param {string} credentials.email - Staff member's email
 * @param {string} credentials.password - Staff member's password
 * @returns {Promise<Object>} The login response containing access token and staff information
 */
export const staffLogin = async ({ email, password }) => {
  try {
    const response = await authApiClient.post('/api/v1/auth/login', {
      email,
      password
    });

    if (response.data.statusCode === "SUCCESS") {
      // Store the access token in sessionStorage
      sessionStorage.setItem("access_token", response.data.response.access_token);
      sessionStorage.setItem("api_key", response.data.response.api_key);

      // Store staff information
      sessionStorage.setItem("staff_info", JSON.stringify(response.data.response.staff));
      sessionStorage.setItem("onboardingComplete", response.data.response.onboardingComplete);

      // Store customerId if it exists in the response
      if (response.data.response.staff.customerId) {
        sessionStorage.setItem("customerId", response.data.response.staff.customerId);
      }

      // Store token expiration time
      const expiresAt = Date.now() + (response.data.response.expires_in * 1000);
      sessionStorage.setItem("token_expires_at", expiresAt.toString());

      // Add a small delay to ensure session storage is set
      await new Promise(resolve => setTimeout(resolve, 100));
    }

    return response.data;
  } catch (error) {
    console.error("Error in staffLogin:", error);
    if (error.response) {
      // The request was made and the server responded with a status code
      // that falls out of the range of 2xx
      console.error('Response data:', error.response.data);
      console.error('Response status:', error.response.status);
      console.error('Response headers:', error.response.headers);
    } else if (error.request) {
      // The request was made but no response was received
      console.error('Request was made but no response received:', error.request);
    } else {
      // Something happened in setting up the request that triggered an Error
      console.error('Error setting up request:', error.message);
    }
    throw error;
  }
};

/**
 * Sends a password reset email to the provided email address
 * @param {string} email - The email address to send the password reset to
 * @returns {Promise<Object>} The password reset response
 */
export const forgotPassword = async (email) => {
  try {
    const response = await authApiClient.post('/api/v1/auth/forget-password', {
      email
    });

    return response.data;
  } catch (error) {
    console.error("Error in forgotPassword:", error);
    if (error.response) {
      // The request was made and the server responded with a status code
      // that falls out of the range of 2xx
      console.error('Response data:', error.response.data);
      console.error('Response status:', error.response.status);
      console.error('Response headers:', error.response.headers);
    } else if (error.request) {
      // The request was made but no response was received
      console.error('Request was made but no response received:', error.request);
    } else {
      // Something happened in setting up the request that triggered an Error
      console.error('Error setting up request:', error.message);
    }
    throw error;
  }
};

/**
 * Submits new customer enquiry information
 * @param {Object} customerData - The customer enquiry data
 * @param {string} customerData.organizationName - Organization name
 * @param {string} customerData.organizationType - Organization type (type1/type2)
 * @param {string} customerData.contactName - Contact person's name
 * @param {string} customerData.phoneNumber - Contact person's phone number
 * @param {string} customerData.email - Contact person's email
 * @returns {Promise<Object>} The enquiry response
 */
export const becomeNewCustomer = async (customerData) => {
  try {
    // Map form data to API expected format
    const payload = {
      customerName: customerData.organizationName,
      customerType: customerData.organizationType === 'type1' ? 'Type 1' : 'Type 2',
      contactPersonName: customerData.contactName,
      contactPersonPhonenumber: customerData.phoneNumber,
      contactPersonEmail: customerData.email
    };

    const response = await authApiClient.post('/api/v1/enquiries', payload);

    return response.data;
  } catch (error) {
    console.error("Error in becomeNewCustomer:", error);
    if (error.response) {
      // The request was made and the server responded with a status code
      // that falls out of the range of 2xx
      console.error('Response data:', error.response.data);
      console.error('Response status:', error.response.status);
      console.error('Response headers:', error.response.headers);
    } else if (error.request) {
      // The request was made but no response was received
      console.error('Request was made but no response received:', error.request);
    } else {
      // Something happened in setting up the request that triggered an Error
      console.error('Error setting up request:', error.message);
    }
    throw error;
  }
};

/**
 * Refreshes the access token using the current token
 * @returns {Promise<Object>} The refresh response containing new access token and staff information
 */
export const refreshToken = async () => {
  try {
    const response = await apiClient.get('/api/v1/auth/refresh-token');

    if (response.data.statusCode === "SUCCESS") {
      // Update the access token in sessionStorage
      sessionStorage.setItem("access_token", response.data.response.access_token);

      // Update staff information
      sessionStorage.setItem("staff_info", JSON.stringify(response.data.response.staff));

      // Update customerId if it exists in the response
      if (response.data.response.staff.customerId) {
        sessionStorage.setItem("customerId", response.data.response.staff.customerId);
      }

      // Update token expiration time
      const expiresAt = Date.now() + (response.data.response.expires_in * 1000);
      sessionStorage.setItem("token_expires_at", expiresAt.toString());
    }

    return response.data;
  } catch (error) {
    console.error("Error in refreshToken:", error);
    throw error;
  }
};

// Facilities API
export const getFacilities = async (params = {}) => {
  try {
    const response = await apiClient.get('/api/v1/facilities/search', { params });
    return response.data.response;
  } catch (error) {
    console.error("Error in getFacilities:", error);
    throw error;
  }
};

export const createFacility = async (facilityData) => {
  try {
    const response = await apiClient.post('/api/v1/facilities', facilityData);
    return response.data;
  } catch (error) {
    console.error("Error in createFacility:", error);
    throw error;
  }
};

/**
 * Updates an existing facility
 * @param {string} id - The facility ID to update
 * @param {Object} facilityData - The updated facility data
 * @returns {Promise<Object>} The updated facility response
 */
/**
 * Gets a facility by ID with departments included
 * @param {string} id - The facility ID to fetch
 * @returns {Promise<Object>} The facility details response
 */
export const getFacilityById = async (id) => {
  try {
    const response = await apiClient.get(`/api/v1/facilities/${id}`, {
      params: { includeDepartments: true }
    });
    return response.data;
  } catch (error) {
    console.error("Error in getFacilityById:", error);
    throw error;
  }
};

export const editFacility = async (id, facilityData) => {
  try {
    const response = await apiClient.put(`/api/v1/facilities/${id}`, facilityData);
    return response.data;
  } catch (error) {
    console.error("Error in editFacility:", error);
    throw error;
  }
};

// Helper function to check if token is expired
export const isTokenExpired = () => {
  const expiryTime = sessionStorage.getItem(API_CONFIG.TOKEN_EXPIRY_KEY);
  if (!expiryTime) return true;
  return Date.now() > parseInt(expiryTime);
};

// Helper function to get current staff info
export const getCurrentStaff = () => {
  const staffInfo = sessionStorage.getItem(API_CONFIG.STAFF_INFO_KEY);
  return staffInfo ? JSON.parse(staffInfo) : null;
};

// Staff API
export const getStaff = async (params = {}) => {
  try {
    const response = await apiClient.get('/api/v1/staff', { params });
    return response.data;
  } catch (error) {
    console.error("Error in getStaff:", error);
    throw error;
  }
};

export const createStaff = async (staffData) => {
  try {
    const response = await apiClient.post('/api/v1/staff', staffData);
    return response.data;
  } catch (error) {
    console.error("Error in createStaff:", error);
    // If the backend returns structured error data, return it instead of throwing
    if (error.response && error.response.data) {
      return error.response.data;
    }
    // For network errors or unexpected errors, still throw
    throw error;
  }
};

export const updateStaff = async (id, staffData) => {
  try {
    const response = await apiClient.put(`/api/v1/staff/${id}`, staffData);
    return response.data ?? response;
  } catch (error) {
    console.error("Error in updateStaff:", error);
    if (error.response && error.response.data) {
      return error.response.data;
    }
    throw error;
  }
};

/**
 * Get staff details by ID for user profile
 * @param {string} staffId - The staff member's ID
 * @returns {Promise<Object>} The staff details response
 */
export const getStaffById = async (staffId) => {
  try {
    const response = await apiClient.get(`/api/v1/staff/${staffId}`);
    return response.data;
  } catch (error) {
    console.error("Error fetching staff details:", error);
    throw error;
  }
};

/**
 * Get facilities by staff ID for user profile
 * @param {string} staffId - The staff member's ID
 * @returns {Promise<Object>} The facilities response
 */
export const getFacilitiesByStaffId = async (staffId) => {
  try {
    const response = await apiClient.get(`/api/v1/facilities/search?staff_id=${staffId}`);
    return response.data;
  } catch (error) {
    console.error("Error fetching facilities by staff ID:", error);
    throw error;
  }
};

/**
 * Get facilities with unassigned resources
 * @returns {Promise<Object>} The facilities response with array of facility IDs
 */
export const getFacilitiesWithUnassignedStaff = async () => {
  try {
    const response = await apiClient.get('/api/v1/facilities/with-unassigned-resources');
    return response.data;
  } catch (error) {
    console.error("Error fetching facilities with unassigned resources:", error);
    throw error;
  }
};

/**
 * Update staff details by ID for user profile
 * @param {string} staffId - The staff member's ID
 * @param {Object} updateData - The data to update
 * @returns {Promise<Object>} The update response
 */
export const updateStaffById = async (staffId, updateData) => {
  try {
    const response = await apiClient.put(`/api/v1/staff/${staffId}`, updateData);
    return response.data;
  } catch (error) {
    console.error("Error updating staff details:", error);
    throw error;
  }
};

/**
 * Updates staff password and/or changePassword flag
 * @param {string} id - The staff member's ID
 * @param {Object} data - The data to update (e.g., { password, changePassword })
 * @returns {Promise<Object>} The API response
 */
export const updateStaffPasswordOrFlag = async (id, data) => {
  try {
    const response = await apiClient.put(`/api/v1/staff/${id}`, data);
    return response.data;
  } catch (error) {
    console.error("Error in updateStaffPasswordOrFlag:", error);
    throw error;
  }
};

// Providers API
export const getProviders = async (params = {}) => {
  try {
    const response = await apiClient.get('/api/v1/rendering-providers/search', { params });
    return response.data.response;
  } catch (error) {
    console.error("Error in getProviders:", error);
    throw error;
  }
};

export const createProvider = async (providerData) => {
  try {
    const response = await apiClient.post('/api/v1/rendering-providers', providerData);
    return response.data;
  } catch (error) {
    console.error("Error in createProvider:", error);
    throw error;
  }
};

export const updateProvider = async (id, providerData) => {
  try {
    const response = await apiClient.put(`/api/v1/rendering-providers/${id}`, providerData);
    return response.data;
  } catch (error) {
    console.error("Error in updateProvider:", error);
    throw error;
  }
};

/**
 * Downloads the bulk provider upload template (Excel file)
 * @returns {Promise<{blob: Blob, filename: string}>} The downloaded template file as a blob and filename
 */
export const downloadProviderBulkTemplate = async () => {
  try {
    const response = await apiClient.get('/api/v1/rendering-providers/bulk-template', {
      responseType: 'blob', // Important for file downloads
      headers: {
        'Accept': 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'
      }
    });
    
    // Extract filename from Content-Disposition header
    let filename = 'provider_bulk_upload_template.xlsx'; // fallback
    const contentDisposition = response.headers['content-disposition'];
    
    if (contentDisposition) {
      const filenameMatch = contentDisposition.match(/filename[^;=\n]*=((['"]).*?\2|[^;\n]*)/);
      if (filenameMatch && filenameMatch[1]) {
        filename = filenameMatch[1].replace(/['"]/g, '');
      }
    }
    
    return { blob: response.data, filename };
  } catch (error) {
    console.error("Error downloading provider bulk template:", error);
    throw error;
  }
};

/**
 * Validates bulk provider data from uploaded Excel file
 * @param {File} file - The Excel file to validate
 * @returns {Promise<Object>} The validation response with results
 */
export const validateProviderBulkData = async (file) => {
  try {
    const formData = new FormData();
    formData.append('file', file);
    
    const response = await apiClient.post('/api/v1/rendering-providers/bulk-validate-file', formData, {
      headers: {
        'Content-Type': 'multipart/form-data'
      }
    });
    
    return response.data;
  } catch (error) {
    console.error("Error validating bulk provider data:", error);
    throw error;
  }
};

/**
 * Revalidates edited bulk provider data (JSON format)
 * @param {Object} editedData - The edited validation data with results array
 * @returns {Promise<Object>} The revalidation response
 */
export const revalidateProviderBulkData = async (editedData) => {
  try {
    const response = await apiClient.post('/api/v1/rendering-providers/bulk-validate-json', editedData);
    return response.data;
  } catch (error) {
    console.error("Error revalidating bulk provider data:", error);
    throw error;
  }
};

/**
 * Creates providers from validated bulk data
 * @param {Object} validationData - The validation response data from bulk-validate
 * @returns {Promise<Object>} The creation response with results
 */
export const createProvidersFromBulkData = async (validationData) => {
  try {
    const response = await apiClient.post('/api/v1/rendering-providers/bulk-create', validationData);
    return response.data;
  } catch (error) {
    console.error("Error creating providers from bulk data:", error);
    throw error;
  }
};

// Department API
export const getDepartments = async (params = {}) => {
  try {
    const response = await apiClient.get('/api/v1/departments/', { params });
    return response.data;
  } catch (error) {
    console.error("Error in getDepartments:", error);
    throw error;
  }
};

export const getDepartmentById = async (departmentId, params = {}) => {
  try {
    const response = await apiClient.get(`/api/v1/departments/${departmentId}`, { params });
    return response.data;
  } catch (error) {
    console.error("Error in getDepartmentById:", error);
    throw error;
  }
};

export const createDepartment = async (departmentData) => {
  try {
    const response = await apiClient.post('/api/v1/departments/', departmentData);
    return response.data;
  } catch (error) {
    console.error("Error in createDepartment:", error);
    throw error;
  }
};

export const updateDepartment = async (id, departmentData) => {
  try {
    const response = await apiClient.put(`/api/v1/departments/${id}`, departmentData);
    return response.data;
  } catch (error) {
    console.error("Error in updateDepartment:", error);
    throw error;
  }
};

export const deleteDepartment = async (id) => {
  try {
    const response = await apiClient.delete(`/api/v1/departments/${id}`);
    return response.data;
  } catch (error) {
    console.error("Error in deleteDepartment:", error);
    throw error;
  }
};

/**
 * Gets departments by facility ID
 * @param {string} facilityId - The facility ID to get departments for
 * @returns {Promise<Object>} The departments response
 */
export const getDepartmentsByFacilityId = async (facilityId) => {
  try {
    const response = await apiClient.get('/api/v1/departments/', {
      params: { facilityId }
    });
    return response.data;
  } catch (error) {
    console.error("Error in getDepartmentsByFacilityId:", error);
    throw error;
  }
};

// Stripe Customer API
export const createStripeCustomer = async () => {
  try {
    const response = await apiClient.get('/api/v1/stripe/customer/create-account');
    if (response.data.statusCode === 'SUCCESS') {
      return response.data.response;
    }
    throw new Error(response.data.error || response.data.message || 'Failed to create Stripe customer');
  } catch (error) {
    console.error('Error in createStripeCustomer:', error);
    throw error;
  }
};

export const lookupStripeCustomer = async () => {
  try {
    const response = await apiClient.get('/api/v1/stripe/customer/lookup');
    if (response.data.statusCode === 'SUCCESS') {
      return response.data.response;
    }
    throw new Error(response.data.error || response.data.message || 'Failed to lookup Stripe customer');
  } catch (error) {
    console.error('Error in lookupStripeCustomer:', error);
    throw error;
  }
};

export const getRemainingUnits = async () => {
  try {
    const response = await apiClient.get(`/api/v1/stripe/customer/remaining-units`);
    if (response.data.statusCode === 'SUCCESS') {
      return response.data.response;
    }
    throw new Error(response.data.error || response.data.message || 'Failed to get remaining units');
  } catch (error) {
    console.error('Error in getRemainingUnits:', error);
    throw error;
  }
};

/**
 * Quick lookup for customer subscription and billing status
 * @returns {Promise<Object>} The quick lookup response with subscription status and remaining units
 */
export const getCustomerQuickLookup = async () => {
  try {
    const response = await apiClient.get('/api/v1/stripe/customer/lookup?quick_check=true');
    if (response.data.statusCode === 'SUCCESS') {
      return response.data.response;
    }
    throw new Error(response.data.error || response.data.message || 'Failed to get customer quick lookup');
  } catch (error) {
    console.error('Error in getCustomerQuickLookup:', error);
    throw error;
  }
};

// Stripe Products API
export const getStripeProducts = async (type = 'MINUTE') => {
  try {
    const response = await apiClient.get(`/api/v1/stripe/products`);
    if (response.data.statusCode === 'SUCCESS') {
      return response.data.response;
    }
    throw new Error(response.data.error || response.data.message || 'Failed to get Stripe products');
  } catch (error) {
    console.error('Error in getStripeProducts:', error);
    throw error;
  }
};

/**
 * Creates a Stripe customer portal session and returns the portal URL
 * @param {string} email - The customer's email
 * @param {string} returnUrl - The URL to return to after managing payment methods
 * @param {string} section - Optional section for the portal session
 * @returns {Promise<{status: string, url: string}>}
 */
export const createStripePortalSession = async (returnUrl, section) => {
  try {
    const body = {
      return_url: returnUrl,
    };
    if (section) body.section = section;
    const response = await apiClient.post('/api/v1/stripe/customer/portal-session', body);
    if (response.data.statusCode === 'SUCCESS') {
      return response.data.response;
    }
    throw new Error(response.data.error || response.data.message || 'Failed to create portal session');
  } catch (error) {
    if (error.response && error.response.data) {
      throw new Error(error.response.data.error || error.response.data.message || 'Failed to create portal session');
    }
    throw error;
  }
};

/**
 * Gets all customer billing settings (plan, units, payment method, billing info)
 * @param {string} email - Customer's email address
 * @returns {Promise<Object>} The customer settings response
 */
export const getCustomerSettings = async (email) => {
  try {
    const response = await apiClient.get(`/api/v1/stripe/customer/settings?email=${encodeURIComponent(email)}`);
    if (response.data.statusCode === 'SUCCESS') {
      return response.data.response;
    }
    throw new Error(response.data.error || response.data.message || 'Failed to get customer settings');
  } catch (error) {
    console.error('Error in getCustomerSettings:', error);
    throw error;
  }
};

/**
 * Gets current billing cycle usage summary
 * @returns {Promise<Object>} The current billing cycle usage summary response
 */
export const getCurrentBillingCycleSummary = async () => {
  try {
    const response = await apiClient.get('/api/v1/usage/records/summary', {
      params: {
        current_billing_cycle: true,
      },
    });
    if (response.data.statusCode === 'SUCCESS') {
      return response.data.response;
    }
    throw new Error(response.data.error || response.data.message || 'Failed to get current billing cycle summary');
  } catch (error) {
    console.error('Error in getCurrentBillingCycleSummary:', error);
    throw error;
  }
};

export const getUsageRecords = async () => {
  try {
    const response = await apiClient.get('/api/v1/usage/records');
    if (response.data.statusCode === 'SUCCESS') {
      return response.data.response;
    }
    throw new Error(response.data.error || response.data.message || 'Failed to get usage records');
  } catch (error) {
    console.error('Error in getUsageRecords:', error);
    throw error;
  }
};

export const createSubscriptionCheckoutSession = async (productId, successUrl, cancelUrl) => {
  try {
    const response = await apiClient.post('/api/v1/stripe/customer/create-subscription-checkout-session', {
      productId,
      successUrl,
      cancelUrl,
    });
    if (response.data.statusCode === 'SUCCESS') {
      return response.data.response;
    }
    throw new Error(response.data.error || response.data.message || 'Failed to create subscription checkout session');
  } catch (error) {
    console.error('Error in createSubscriptionCheckoutSession:', error);
    throw error;
  }
};

export const finalizeSubscription = async (sessionId) => {
  try {
    const response = await apiClient.get(`/api/v1/stripe/customer/finalize-subscription?stripe_session_id=${encodeURIComponent(sessionId)}`);
    if (response.data.statusCode === 'SUCCESS') {
      return response.data.response;
    }
    throw new Error(response.data.error || response.data.message || 'Failed to finalize subscription');
  } catch (error) {
    console.error('Error in finalizeSubscription:', error);
    throw error;
  }
};

/**
 * Submits enterprise contact form data
 * @param {Object} contactData - The contact form data
 * @param {string} contactData.name - Contact's name
 * @param {string} contactData.phoneNumber - Contact's phone number
 * @param {string} contactData.emailId - Contact's email
 * @param {string} contactData.companySize - Company size
 * @param {number} contactData.monthlyMinutes - Required monthly minutes
 * @param {string} contactData.message - Optional message
 * @returns {Promise<Object>} The submission response
 */
export const submitEnterpriseContact = async (contactData) => {
  try {
    const response = await apiClient.post('/api/v1/stripe/contact-sales', contactData);
    if (response.data.statusCode === 'SUCCESS') {
      return response.data;
    }
    throw new Error(response.data.error || response.data.message || 'Failed to submit enterprise contact form');
  } catch (error) {
    console.error('Error in submitEnterpriseContact:', error);
    throw error;
  }
};

export default apiClient;

// ============================================
// Call History API Functions
// ============================================

/**
 * Gets list of all call requests
 * @param {Object} params - Optional query parameters for filtering
 * @param {string} [params.batchId] - Batch ID filter
 * @param {number} [params.limit] - Number of records to return (defaults to 100000 for backward compatibility)
 * @param {number} [params.skip] - Number of records to skip (for pagination)
 * @param {string} [params.patientName] - Patient name search filter
 * @param {string} [params.callStatus] - Call status filter (scheduled, cancelled, successful, failure)
 * @returns {Promise<Object>} The call list response
 */
export const getCallsList = async (params = {}) => {
  try {
    // Build query parameters, only including defined values
    const queryParams = {};
    
    if (params.batchId) queryParams.batch_id = params.batchId;
    
    // If limit is explicitly passed, use it; otherwise default to 100000 for backward compatibility
    // (Some components like Home.js expect all records for client-side pagination)
    queryParams.limit = params.limit !== undefined ? params.limit : 100000;
    
    if (params.skip !== undefined) queryParams.skip = params.skip;
    if (params.patientName) queryParams.patient_name = params.patientName;
    // Backend expects 'call_status' with values: scheduled, cancelled, successful, failure
    if (params.callStatus) queryParams.call_status = params.callStatus;
    
    const response = await apiClient.get('/api/v1/calls/list', { params: queryParams });
    return response.data;
  } catch (error) {
    console.error("Error in getCallsList:", error);
    throw error;
  }
};

/**
 * Gets detailed call information by call request ID
 * @param {string} callRequestId - The call request ID
 * @returns {Promise<Object>} The detailed call response
 */
export const getCallRequest = async (callRequestId) => {
  try {
    const response = await apiClient.get('/api/v1/calls/call-request', {
      params: { call_request_id: callRequestId }
    });
    return response.data;
  } catch (error) {
    console.error("Error in getCallRequest:", error);
    throw error;
  }
};

/**
 * Gets complete call details with transcription by call request ID (New format)
 * @param {string} callRequestId - The call request ID
 * @returns {Promise<Object>} The complete call details response with transcription
 */
export const getCallDetails = async (callRequestId) => {
  try {
    const response = await apiClient.get('/api/v1/calls/call-request', {
      params: { call_request_id: callRequestId }
    });
    return response.data;
  } catch (error) {
    console.error("Error in getCallDetails:", error);
    throw error;
  }
};

/**
 * Gets ALL calls for a specific call request (all attempts)
 * @param {string} callRequestId - The call request ID
 * @returns {Promise<Object>} The response with all calls, whitesheet, and batch metadata
 */
export const getAllCallsForRequest = async (callRequestId) => {
  try {
    const response = await apiClient.get('/api/v1/calls/call-request/all-calls', {
      params: { call_request_id: callRequestId, only_success: true }
    });
    
    return response.data;
  } catch (error) {
    console.error("Error in getAllCallsForRequest:", error);
    throw error;
  }
};

/**
 * Gets call audio URL
 * @param {string} audioUrl - The audio URL to fetch
 * @returns {Promise<Object>} The audio response
 */
export const getCallAudio = async (audioUrl) => {
  try {
    const response = await apiClient.post('/api/v1/calls/audio', {
      audio_url: audioUrl
    });
    return response.data;
  } catch (error) {
    console.error("Error in getCallAudio:", error);
    throw error;
  }
};

/**
 * Gets call recording URL by call ID
 * @param {string} callId - The call ID
 * @returns {Promise<Object>} The recording response
 */
export const getCallRecording = async (callId) => {
  try {
    // Add cache-busting parameter to prevent browser caching of pre-signed URLs
    const cacheBuster = Date.now();
    const response = await apiClient.get(`/api/v1/calls/${callId}/recording`, {
      params: { _t: cacheBuster },
      headers: {
        'Cache-Control': 'no-cache, no-store, must-revalidate',
        'Pragma': 'no-cache',
        'Expires': '0'
      }
    });
    return response.data;
  } catch (error) {
    console.error("Error in getCallRecording:", error);
    throw error;
  }
};

/**
 * Downloads whitesheet for a specific call request
 * @param {string} callId - The call request ID
 * @param {string} fileFormat - The format for the download (default: 'pdf')
 * @returns {Promise<{blob: Blob, filename: string}>} The downloaded file as a blob and filename
 */
export const downloadWhitesheet = async (callId, fileFormat = 'pdf') => {
  try {
    const response = await apiClient.get(`/api/v1/call-requests/${callId}/whitesheet/download`, {
      params: { file_format: fileFormat },
      responseType: 'blob', // Important for file downloads
      headers: {
        'Accept': 'application/pdf'
      }
    });

    // Extract filename from Content-Disposition header
    let filename = `whitesheet_${callId}.${fileFormat === 'excel' ? 'xlsx' : 'pdf'}`; // fallback
    const contentDisposition = response.headers['content-disposition'];

    if (contentDisposition) {
      const filenameMatch = contentDisposition.match(/filename=(.+)/);
      if (filenameMatch && filenameMatch[1]) {
        filename = filenameMatch[1].trim();
      }
    }

    console.log('Backend filename info:', contentDisposition);
    console.log('Extracted filename:', filename);

    return { blob: response.data, filename };
  } catch (error) {
    console.error(`Error downloading whitesheet for call ${callId}:`, error);
    throw error;
  }
};

/**
 * Downloads whitesheet for a specific batch
 * @param {string} batchId - The batch ID
 * @param {string} fileFormat - The format for the download (default: 'excel')
 * @returns {Promise<{blob: Blob, filename: string}>} The downloaded file as a blob and filename
 */
export const downloadBatchWhitesheet = async (batchId, fileFormat = 'excel') => {
  try {
    const response = await apiClient.get(`/api/v1/batches/${batchId}/whitesheet/download`, {
      params: { file_format: fileFormat, allowIncomplete: true },
      responseType: 'blob', // Important for file downloads
      headers: {
        'Accept': fileFormat === 'excel' ? 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' : 'application/pdf'
      }
    });

    // Extract filename from Content-Disposition header
    let filename = `batch_${batchId}_ai_call_summary.${fileFormat === 'excel' ? 'xlsx' : 'pdf'}`; // fallback
    const contentDisposition = response.headers['content-disposition'];

    if (contentDisposition) {
      const filenameMatch = contentDisposition.match(/filename=(.+)/);
      if (filenameMatch && filenameMatch[1]) {
        filename = filenameMatch[1].trim();
      }
    }

    console.log('Backend filename info:', contentDisposition);
    console.log('Extracted filename:', filename);

    return { blob: response.data, filename };
  } catch (error) {
    console.error(`Error downloading batch whitesheet for batch ${batchId}:`, error);
    throw error;
  }
};

export const downloadBatchAllCallDetails = async (batchId, fileFormat = 'zip') => {
  try {
    const response = await apiClient.get(`/api/v1/batches/${batchId}/whitesheet/download`, {
      params: { file_format: fileFormat, allowIncomplete: true },
      responseType: 'blob', // Important for file downloads
      headers: {
        'Accept': 'application/zip'
      }
    });
    
    // Extract filename from Content-Disposition header
    let filename = `batch_${batchId}_all_call_ai_details.zip`; // fallback
    const contentDisposition = response.headers['content-disposition'];
    
    if (contentDisposition) {
      const filenameMatch = contentDisposition.match(/filename=(.+)/);
      if (filenameMatch && filenameMatch[1]) {
        filename = filenameMatch[1].trim();
      }
    }
    
    console.log('Backend filename info for all call details:', contentDisposition);
    console.log('Extracted filename:', filename);
    
    return { blob: response.data, filename };
  } catch (error) {
    console.error(`Error downloading all call AI details for batch ${batchId}:`, error);
    throw error;
  }
};

/**
 * Gets available time slots for reattempting calls
 * @param {Object} params - The request parameters
 * @param {string} params.providerTimezone - The provider's timezone (e.g., "America/Los_Angeles")
 * @param {string} params.localDate - The local date in MM/DD/YYYY format
 * @returns {Promise<Object>} The slot availability response
 */
export const getSlotAvailability = async ({ providerTimezone, localDate }) => {
  try {
    const response = await apiClient.post('/api/v1/batches/slots/availability', {
      providerTimezone,
      localDate
    });
    return response.data;
  } catch (error) {
    console.error("Error in getSlotAvailability:", error);
    throw error;
  }
};

/**
 * Reschedules a call to a new time slot
 * @param {Object} params - The request parameters
 * @param {string} params.callRequestId - The call request ID to reschedule
 * @param {string} params.startDateTime - The new start date time in UTC format (from utcStartTime)
 * @returns {Promise<Object>} The reschedule response
 */
export const rescheduleCall = async ({ callRequestId, startDateTime }) => {
  try {
    const response = await apiClient.post('/api/v1/batches/re-schedule', {
      callRequestId,
      startDateTime
    });
    return response.data;
  } catch (error) {
    console.error("Error in rescheduleCall:", error);
    throw error;
  }
};

/**
 * Gets customer details including use cases and subscription information
 * @returns {Promise<Object>} The customer details response
 */
export const getCustomerDetails = async () => {
  try {
    const response = await apiClient.get('/api/v1/customers');
    if (response.data.statusCode === 'SUCCESS') {
      return response.data; 
    }
    throw new Error(response.data.error || response.data.message || 'Failed to get customer details');
  } catch (error) {
    console.error('Error in getCustomerDetails:', error);
    throw error;
  }
};

/**
 * Gets questions by path and type
 * @param {Object} params - The request parameters
 * @param {string} params.path - The path in format 'branch-usecase-subusecase'
 * @param {string} params.type - The type of questions to fetch (e.g., 'custom', 'standard')
 * @returns {Promise<Object>} The questions response
 */
export const getQuestionsByPathAndType = async ({ path, type }) => {
  try {
    const response = await apiClient.post('/api/v1/questions/get-questions', {
      path,
      type
    });
    if (response.data.statusCode === 'SUCCESS') {
      return response.data.response;
    }
    throw new Error(response.data.error || response.data.message || 'Failed to get questions');
  } catch (error) {
    console.error('Error in getQuestionsByPathAndType:', error);
    throw error;
  }
};

/**
 * Creates custom questions for a specific path
 * @param {Object} params - The request parameters
 * @param {string} params.path - The path in format 'branch-usecase-subusecase'
 * @param {Array<Object>} params.questions - Array of questions to create
 * @param {string} params.questions[].question - The question text
 * @param {string} params.questions[].expected_value - The expected value type
 * @returns {Promise<Object>} The creation response
 */
export const createCustomQuestions = async ({ path, questions }) => {
  try {
    const response = await apiClient.post('/api/v1/questions/create-custom-questions', {
      path,
      questions
    });
    if (response.data.statusCode === 'SUCCESS') {
      return response.data.response;
    }
    throw new Error(response.data.error || response.data.message || 'Failed to create custom questions');
  } catch (error) {
    console.error('Error in createCustomQuestions:', error);
    throw error;
  }
};

/**
 * Validates custom questions before creation
 * @param {Object} params - The request parameters
 * @param {Array<Object>} params.questions - Array of questions to validate
 * @param {string} params.questions[].question - The question text
 * @param {string} params.questions[].id - Optional question ID
 * @param {string} params.branch - The branch name
 * @param {string} params.usecase - The usecase name
 * @param {string} params.subusecase - The subusecase name
 * @returns {Promise<Object>} The validation response
 */
export const validateCustomQuestions = async ({ questions, branch, usecase, subusecase }) => {
  try {
    const response = await apiClient.post('/api/v1/questions/validate-custom-questions', {
      questions,
      branch,
      usecase,
      subusecase
    });
    if (response.data.statusCode === 'SUCCESS') {
      return response.data.response;
    }
    throw new Error(response.data.error || response.data.message || 'Failed to validate custom questions');
  } catch (error) {
    console.error('Error in validateCustomQuestions:', error);
    throw error;
  }
};

/**
 * Validates multiple patient files data with automatic retry on 504 Gateway Timeout
 * 
 * RETRY LOGIC FOR 504 GATEWAY TIMEOUT:
 * ------------------------------------
 * This endpoint can timeout during large data validation, but the backend implements 
 * intelligent caching. When a 504 occurs:
 * 
 * 1. Backend has already started processing and caches the operation
 * 2. Subsequent retries with the EXACT SAME data will hit the cache
 * 3. If processing completed, cached response is returned immediately
 * 4. This prevents duplicate processing while ensuring eventual success
 * 
 * BEHAVIOR:
 * - Retries up to maxRetries times (default: 3) ONLY for 504 errors
 * - Adds 2 second delay between retries to allow backend caching to complete
 * - For ANY other error (400, 401, 500, etc.), throws immediately without retry
 * - Each retry uses the same payload, leveraging backend's cache
 * 
 * @param {Object} params - The validation parameters
 * @param {Object} params.metadata - The metadata containing branch, usecase, and subUsecase
 * @param {Array} params.patientFileData - Array of file data objects containing filename and data
 * @param {number} [maxRetries=3] - Maximum number of retry attempts for 504 errors
 * @returns {Promise<Object>} The validation response
 */
export const validatePatientFiles = async ({ metadata, patientFileData }, maxRetries = 6) => {
  let lastError = null;
  
  // Retry loop - will attempt up to maxRetries times
  for (let attempt = 1; attempt <= maxRetries; attempt++) {
    try {
      console.log(`🔍 Validation attempt ${attempt} of ${maxRetries}...`);
      
      const response = await apiClient.post('/api/v1/validation', {
        metadata,
        patientFileData
      });
      
      console.log(`✅ Validation successful on attempt ${attempt}`);
      return response.data;
      
    } catch (error) {
      lastError = error;
      const statusCode = error.response?.status;
      
      // Check if it's a 504 Gateway Timeout error
      if (statusCode === 504) {
        console.warn(`⏱️ 504 Gateway Timeout on attempt ${attempt} of ${maxRetries}`);
        
        // If we haven't exhausted all retries, try again
        if (attempt < maxRetries) {
          console.log(`🔄 Retrying validation (backend has cached the process)...`);
          // Add a delay before retry to allow backend caching to complete
          await new Promise(resolve => setTimeout(resolve, 2000));
          continue; // Try again
        } else {
          console.error(`❌ All ${maxRetries} attempts failed with 504 errors`);
        }
      } else {
        // For non-504 errors, don't retry - throw immediately
        console.error(`❌ Validation failed with status ${statusCode || 'unknown'}:`, error);
        throw error;
      }
    }
  }
  
  // If we exhausted all retries, throw the last error
  console.error('Error validating patient files after all retries:', lastError);
  throw lastError;
};

/**
 * Gets EHR endpoint configuration for all EHR systems
 * Shows which use cases are supported and what fields are required/optional
 * @returns {Promise<Object>} The EHR configuration object
 */
export const getEhrEndpointConfig = async () => {
  try {
    const response = await apiClient.get('/api/v1/ehr/ehr-endpoint-config');
    return response.data;
  } catch (error) {
    console.error('Error fetching EHR endpoint config:', error);
    throw error;
  }
};

/**
 * Gets required fields for EHR connection per EHR system
 * @returns {Promise<Object>} The ehr_connection_required_fields object keyed by EHR name
 */
export const getEhrConnectionRequiredFields = async () => {
  try {
    const response = await apiClient.get('/api/v1/ehr/connections/required-fields');
    const data = response.data;
    return data?.response?.ehr_connection_required_fields ?? data?.response ?? {};
  } catch (error) {
    console.error('Error fetching EHR connection required fields:', error);
    throw error;
  }
};

/**
 * Creates an EHR connection for a facility
 * @param {Object} payload - { facilityId, ehrName, ...dynamicFields }
 * @returns {Promise<Object>} The create response
 */
export const createEhrConnection = async (payload) => {
  try {
    const response = await apiClient.post('/api/v1/ehr/connections', payload);
    return response.data;
  } catch (error) {
    console.error('Error creating EHR connection:', error);
    throw error;
  }
};

/**
 * Query EHR data dynamically (offices, locations, providers, referring_providers)
 * @param {string} queryType - Type of query: 'offices', 'locations', 'providers', 'referring_providers'
 * @param {string} ehrName - The name of the EHR system
 * @returns {Promise<Object>} The response containing queried data
 */
export const queryEhrData = async (queryType, ehrName, facilityId) => {
  try {
    const response = await apiClient.get('/api/v1/ehr/query-ehr-data', {
      params: {
        query_type: queryType,
        ehr_name: ehrName,
        facility_id: facilityId
      }
    });
    return response.data;
  } catch (error) {
    console.error(`Error querying EHR data (${queryType}):`, error);
    throw error;
  }
};

/**
 * Query offices for a specific EHR system (Legacy - use queryEhrData instead)
 * @param {string} ehrName - The name of the EHR system
 * @returns {Promise<Object>} The response containing office list
 */
export const queryEhrOffices = async (ehrName) => {
  try {
    const response = await apiClient.get('/api/v1/ehr/query-offices', {
      params: {
        ehr_name: ehrName
      }
    });
    return response.data;
  } catch (error) {
    console.error('Error querying EHR offices:', error);
    throw error;
  }
};

/**
 * Polls the EHR data fetch status endpoint until completion
 * @param {string} jobId - The job ID to poll
 * @param {number} maxAttempts - Maximum number of polling attempts (default: 60)
 * @param {number} intervalMs - Polling interval in milliseconds (default: 10000 = 10 seconds)
 * @param {number} currentAttempt - Current attempt number (used internally for recursion)
 * @returns {Promise<Object>} The final EHR data response
 */
const pollEhrDataStatus = async (jobId, maxAttempts = 60, intervalMs = 10000, currentAttempt = 1) => {
  try {
    console.log(`📊 Polling EHR data status (attempt ${currentAttempt}/${maxAttempts})...`);
    
    const response = await apiClient.get(`/api/v1/ehr/fetch-ehr-patients-data-status/${jobId}`);
    
    const { statusCode } = response.data;
    
    // Check if processing is complete
    if (statusCode === 'SUCCESS') {
      console.log('✅ EHR data fetch completed successfully!');
      return response.data;
    }
    
    // Check if still processing (statusCode: "PROCESSING" or status code 202)
    if (statusCode === 'PROCESSING' || response.status === 202) {
      // Check if we've exceeded max attempts
      if (currentAttempt >= maxAttempts) {
        throw new Error(`Polling timeout: EHR data fetch exceeded maximum attempts (${maxAttempts})`);
      }
      
      console.log(`⏳ EHR data still processing. Waiting ${intervalMs / 1000} seconds before next poll...`);
      
      // Wait for the specified interval before polling again
      await new Promise(resolve => setTimeout(resolve, intervalMs));
      
      // Recursively poll again
      return pollEhrDataStatus(jobId, maxAttempts, intervalMs, currentAttempt + 1);
    }
    
    // Check for failure
    if (statusCode === 'FAILED' || statusCode === 'ERROR') {
      throw new Error(response.data.message || 'EHR data fetch failed');
    }
    
    // Unexpected status
    throw new Error(`Unexpected status: ${statusCode}`);
    
  } catch (error) {
    console.error(`❌ Error polling EHR data status (attempt ${currentAttempt}):`, error);
    
    // If it's a network error and we haven't exceeded max attempts, retry
    if (error.response?.status === 502 || error.response?.status === 504 || error.code === 'ECONNABORTED') {
      if (currentAttempt < maxAttempts) {
        console.log(`🔄 Network error, retrying in ${intervalMs / 1000} seconds...`);
        await new Promise(resolve => setTimeout(resolve, intervalMs));
        return pollEhrDataStatus(jobId, maxAttempts, intervalMs, currentAttempt + 1);
      }
    }
    
    throw error;
  }
};

/**
 * Fetches EHR patients data with automatic polling
 * @param {Object} params - The fetch parameters
 * @returns {Promise<Object>} The EHR patients data response
 */
export const fetchEhrPatientsData = async ({ 
  branch, 
  usecase = 'benefits_verification', 
  sub_usecase = 'default',
  ehr_name,
  start_date,
  end_date,
  office_id = null,
  provider_id = null,
  patient_ids = null,
  referring_provider_id = null,
  location_id = null,
  as_of_date = null,
  aging_days = null,
  include_payment_claims = null,
  date_of_service = null,
  document_type_id = null,
  appointment_type_id = null,
  appointment_type_ids = null,
  department_id = null,
  state = null,
  facility_id = null,
  onPollingUpdate = null // Optional callback for polling progress updates
}) => {
  try {
    const payload = {
      branch,
      usecase,
      sub_usecase,
      ehr_name,
    };
    
    // Add required fields
    if (start_date) payload.start_date = start_date;
    if (end_date) payload.end_date = end_date;
    
    // Add optional/dynamic fields only if provided
    if (office_id) payload.office_id = office_id;
    if (provider_id) payload.provider_id = provider_id;
    if (patient_ids && Array.isArray(patient_ids) && patient_ids.length > 0) {
      payload.patient_ids = patient_ids;
    }
    if (referring_provider_id) payload.referring_provider_id = referring_provider_id;
    if (location_id) payload.location_id = location_id;
    if (as_of_date) payload.as_of_date = as_of_date;
    if (aging_days !== null && aging_days !== undefined) payload.aging_days = aging_days;
    if (include_payment_claims !== null && include_payment_claims !== undefined) {
      payload.include_payment_claims = include_payment_claims;
    }
    if (date_of_service) payload.date_of_service = date_of_service;
    if (
      document_type_id !== null &&
      document_type_id !== undefined &&
      `${document_type_id}`.trim() !== ""
    ) {
      payload.document_type_id = `${document_type_id}`;
    }
    if (
      appointment_type_id !== null &&
      appointment_type_id !== undefined &&
      `${appointment_type_id}`.trim() !== ""
    ) {
      payload.appointment_type_id = `${appointment_type_id}`;
    }
    if (
      appointment_type_ids !== null &&
      appointment_type_ids !== undefined &&
      `${appointment_type_ids}`.trim() !== ""
    ) {
      payload.appointment_type_id = `${appointment_type_ids}`;
    }
    if (
      department_id !== null &&
      department_id !== undefined &&
      `${department_id}`.trim() !== ""
    ) {
      payload.department_id = `${department_id}`;
    }
    payload.state =
      state !== null &&
      state !== undefined &&
      `${state}`.trim() !== ""
        ? `${state}`.trim()
        : null;
    if (facility_id) payload.facility_id = facility_id;
    
    console.log('🚀 Starting EHR data fetch...', payload);
    
    // Step 1: Initiate the EHR data fetch
    const initiateResponse = await apiClient.post('/api/v1/ehr/fetch-ehr-patients-data', payload);
    
    const { statusCode, response: initiateData, message } = initiateResponse.data;
    
    // Check if job was initiated successfully
    if (statusCode !== 'SUCCESS' || !initiateData?.job_id) {
      throw new Error(message || 'Failed to initiate EHR data fetch');
    }
    
    const jobId = initiateData.job_id;
    console.log(`✅ EHR data fetch initiated. Job ID: ${jobId}`);
    console.log(`📊 Status: ${initiateData.status}`);
    console.log(`📝 ${message}`);
    
    // Notify caller about polling start (if callback provided)
    if (onPollingUpdate) {
      onPollingUpdate({
        status: 'initiated',
        jobId: jobId,
        message: message
      });
    }
    
    // Step 2: Poll for completion
    const pollResponse = await pollEhrDataStatus(jobId);
    
    // Return the poll response as-is (response structure: { statusCode, response: [...], message })
    return pollResponse;
    
  } catch (error) {
    console.error('❌ Error in fetchEhrPatientsData:', error);
    throw error;
  }
};

/**
 * Uploads batch files for validation with automatic retry on 504 Gateway Timeout
 * 
 * RETRY LOGIC FOR 504 GATEWAY TIMEOUT:
 * ------------------------------------
 * This endpoint can timeout on slow networks or large file uploads, but the backend 
 * implements intelligent caching. When a 504 occurs:
 * 
 * 1. Backend has already started processing and caches the operation
 * 2. Subsequent retries with the EXACT SAME data will hit the cache
 * 3. If processing completed, cached response is returned immediately
 * 4. This prevents duplicate processing while ensuring eventual success
 * 
 * BEHAVIOR:
 * - Retries up to maxRetries times (default: 3) ONLY for 504 errors
 * - Adds 1 second delay between retries to allow backend caching to complete
 * - For ANY other error (400, 401, 500, etc.), throws immediately without retry
 * - Each retry uses the same FormData, leveraging backend's cache
 * 
 * @param {FormData} formData - The FormData containing files and metadata
 * @param {number} [maxRetries=3] - Maximum number of retry attempts for 504 errors
 * @returns {Promise<Object>} API response with validation results
 */
export const uploadBatchFiles = async (formData, maxRetries = 6) => {
  let lastError = null;
  
  // Retry loop - will attempt up to maxRetries times
  for (let attempt = 1; attempt <= maxRetries; attempt++) {
    try {
      console.log(`📤 Upload attempt ${attempt} of ${maxRetries}...`);
      
      // Create a separate axios instance for file upload with FormData
      const uploadClient = axios.create({
        baseURL: API_CONFIG.BASE_URL,
        headers: {
          'Accept': 'application/json',
          'Cache-Control': 'no-cache, no-store, must-revalidate',
          'Pragma': 'no-cache',
          'Expires': '0'
        }
      });

      // Add auth token to headers
      const token = sessionStorage.getItem(API_CONFIG.AUTH_TOKEN_KEY);
      if (token) {
        uploadClient.defaults.headers.Authorization = `Bearer ${token}`;
      }

      // Add API key to headers
      const apiKey = sessionStorage.getItem("api_key");
      if (apiKey) {
        uploadClient.defaults.headers['x-apikey'] = apiKey;
      }

      const response = await uploadClient.post('/api/v1/validation/upload-batch-files', formData, {
        headers: {
          ...formData.getHeaders ? formData.getHeaders() : {},
          'Content-Type': 'multipart/form-data'
        }
      });

      console.log(`✅ Upload successful on attempt ${attempt}`);
      return response.data;
      
    } catch (error) {
      lastError = error;
      const statusCode = error.response?.status;
      
      // Check if it's a 504 Gateway Timeout error
      if (statusCode === 504) {
        console.warn(`⏱️ 504 Gateway Timeout on attempt ${attempt} of ${maxRetries}`);
        
        // If we haven't exhausted all retries, try again
        if (attempt < maxRetries) {
          console.log(`🔄 Retrying upload (backend has cached the process)...`);
          // Add a small delay before retry to allow backend caching to complete
          await new Promise(resolve => setTimeout(resolve, 1000));
          continue; // Try again
        } else {
          console.error(`❌ All ${maxRetries} attempts failed with 504 errors`);
        }
      } else {
        // For non-504 errors, don't retry - throw immediately
        console.error(`❌ Upload failed with status ${statusCode || 'unknown'}:`, error);
        throw error;
      }
    }
  }
  
  // If we exhausted all retries, throw the last error
  console.error('Error uploading batch files after all retries:', lastError);
  throw lastError;
};

/**
 * Schedules a batch of calls
 * @param {Object} params - The request parameters
 * @param {Object} params.metadata - The metadata for the batch
 * @param {string} params.metadata.startDateTime - The start date time in ISO format
 * @param {string} params.metadata.branch - The branch name
 * @param {string} params.metadata.usecase - The usecase name
 * @param {string} params.metadata.subUsecase - The subusecase name
 * @param {string} params.metadata.uploadedAt - The upload timestamp in ISO format
 * @param {string} params.metadata.providerTimezone - The provider's timezone
 * @param {Array<string>} params.metadata.customQuestionIds - Array of custom question IDs
 * @param {string} params.metadata.source - The source of the batch (e.g., "manual")
 * @param {Array<Object>} params.data - Array of patient data objects
 * @returns {Promise<Object>} The schedule response
 */
export const scheduleBatch = async ({ metadata, data }) => {
  try {
    const response = await apiClient.post('/api/v1/batches/schedule', {
      metadata,
      data
    });
    if (response.data.statusCode === 'SUCCESS') {
      return response.data;
    }
    throw new Error(response.data.error || response.data.message || 'Failed to schedule batch');
  } catch (error) {
    console.error('Error in scheduleBatch:', error);
    throw error;
  }
};

/**
 * Checks if a batch alias is unique within customer scope
 * @param {string} alias - The alias to check
 * @returns {Promise<Object>} The alias uniqueness check response
 */
export const checkBatchAliasUnique = async (alias) => {
  try {
    const response = await apiClient.get(`/api/v1/batches/check-alias/${encodeURIComponent(alias)}`);
    if (response.data.statusCode === 'SUCCESS') {
      return response.data;
    }
    throw new Error(response.data.error || response.data.message || 'Failed to check alias uniqueness');
  } catch (error) {
    console.error('Error in checkBatchAliasUnique:', error);
    throw error;
  }
};

/**
 * Generates a unique, professional batch alias using AI
 * @param {Object} params - The generation parameters
 * @param {string} params.branch - Medical branch (e.g., "medical", "dental")
 * @param {string} params.usecase - Primary use case (e.g., "prior_authorization", "billing")
 * @param {string} params.subusecase - Sub use case (e.g., "request_follow_up", "claim_review")
 * @returns {Promise<Object>} The AI-generated alias response
 */
export const generateBatchAlias = async ({ branch, usecase, subusecase }) => {
  try {
    const response = await apiClient.post('/api/v1/batches/generate-alias', {
      branch,
      usecase,
      subusecase
    });
    if (response.data.statusCode === 'SUCCESS') {
      return response.data;
    }
    throw new Error(response.data.error || response.data.message || 'Failed to generate batch alias');
  } catch (error) {
    console.error('Error in generateBatchAlias:', error);
    throw error;
  }
};

/**
 * Gets column headers for a specific use case path with automatic retry on 504 Gateway Timeout
 * 
 * RETRY LOGIC FOR 504 GATEWAY TIMEOUT:
 * ------------------------------------
 * This endpoint can timeout during column header generation, but the backend implements 
 * intelligent caching. When a 504 occurs:
 * 
 * 1. Backend has already started processing and caches the operation
 * 2. Subsequent retries with the EXACT SAME path will hit the cache
 * 3. If processing completed, cached response is returned immediately
 * 4. This prevents duplicate processing while ensuring eventual success
 * 
 * BEHAVIOR:
 * - Retries up to maxRetries times (default: 3) ONLY for 504 errors
 * - Adds 2 second delay between retries to allow backend caching to complete
 * - For ANY other error (400, 401, 500, etc.), throws immediately without retry
 * - Each retry uses the same path, leveraging backend's cache
 * 
 * @param {string} path - The path in format "branch-usecase-subUsecase"
 * @param {number} [maxRetries=3] - Maximum number of retry attempts for 504 errors
 * @returns {Promise<Object>} The column headers response
 */
export const getColumnHeaders = async (path, maxRetries = 6) => {
  let lastError = null;
  
  // Retry loop - will attempt up to maxRetries times
  for (let attempt = 1; attempt <= maxRetries; attempt++) {
    try {
      console.log(`📋 Column headers attempt ${attempt} of ${maxRetries}...`);
      
      const response = await apiClient.post('/api/v1/validation/get-column-headers', {
        path
      });
      
      if (response.data.statusCode === 'SUCCESS') {
        console.log(`✅ Column headers retrieved successfully on attempt ${attempt}`);
        return response.data;
      }
      throw new Error(response.data.error || response.data.message || 'Failed to get column headers');
      
    } catch (error) {
      lastError = error;
      const statusCode = error.response?.status;
      
      // Check if it's a 504 Gateway Timeout error
      if (statusCode === 504) {
        console.warn(`⏱️ 504 Gateway Timeout on attempt ${attempt} of ${maxRetries}`);
        
        // If we haven't exhausted all retries, try again
        if (attempt < maxRetries) {
          console.log(`🔄 Retrying column headers fetch (backend has cached the process)...`);
          // Add a delay before retry to allow backend caching to complete
          await new Promise(resolve => setTimeout(resolve, 2000));
          continue; // Try again
        } else {
          console.error(`❌ All ${maxRetries} attempts failed with 504 errors`);
        }
      } else {
        // For non-504 errors, don't retry - throw immediately
        console.error(`❌ Column headers fetch failed with status ${statusCode || 'unknown'}:`, error);
        throw error;
      }
    }
  }
  
  // If we exhausted all retries, throw the last error
  console.error('Error in getColumnHeaders after all retries:', lastError);
  throw lastError;
};

/**
 * Gets batches with optional pagination and filters
 * @param {Object} params - The query parameters
 * @param {number} params.skip - Number of records to skip
 * @param {number} params.limit - Number of records to fetch
 * @param {string} [params.batch_id] - Optional batch ID to filter
 * @param {string} [params.status] - Optional status filter (scheduled, completed, cancelled)
 * @param {string} [params.branch] - Optional branch filter
 * @param {string} [params.usecase] - Optional usecase filter
 * @param {string} [params.subUsecase] - Optional sub-usecase filter
 * @param {string} [params.uploaderName] - Optional uploader name filter
 * @param {string} [params.search] - Optional search query for batch alias/name
 * @returns {Promise<Object>} The batches response
 */
export const getBatches = async (params = {}) => {
  try {
    const { skip = 0, limit = 100, ...filterParams } = params;
    
    // Build query params, filtering out undefined/null values
    const queryParams = { skip, limit };
    Object.entries(filterParams).forEach(([key, value]) => {
      if (value !== undefined && value !== null && value !== '') {
        queryParams[key] = value;
      }
    });
    
    const response = await apiClient.get('/api/v1/batches/get-batches', {
      params: queryParams
    });
    if (response.data.statusCode === 'SUCCESS') {
      return response.data.response;
    }
    throw new Error(response.data.error || response.data.message || 'Failed to get batches');
  } catch (error) {
    console.error('Error in getBatches:', error);
    throw error;
  }
};

/**
 * Cancels calls or a batch
 * @param {Object} params - The cancellation parameters
 * @param {('call'|'batch')} params.cancelType - What to cancel: 'calls' or 'batch'
 * @param {Array<string>} params.ids - Array of IDs to cancel (use a single id for batch cancellation in UI)
 * @returns {Promise<Object>} The cancellation response
 */
export const cancelCallsOrBatch = async ({ cancelType, ids }) => {
  try {
    if (cancelType !== 'call' && cancelType !== 'batch') {
      throw new Error("Invalid cancelType. Must be 'calls' or 'batch'.");
    }
    if (!Array.isArray(ids) || ids.length === 0) {
      throw new Error('ids must be a non-empty array');
    }

    const response = await apiClient.post('/api/v1/batches/cancel', {
      cancelType,
      // Backend expects capital 'Ids' per spec
      Ids: ids,
    });
    return response.data;
  } catch (error) {
    console.error('Error in cancelCallsOrBatch:', error);
    throw error;
  }
};


/**
 * Gets Google OAuth authorization URL
 * @returns {Promise<Object>} The response containing auth_url and state
 */
export const getGoogleAuthUrl = async () => {
  try {
    const response = await authApiClient.get('/api/v1/auth/google/auth-url');
    return response.data;
  } catch (error) {
    console.error("Error in getGoogleAuthUrl:", error);
    throw error;
  }
};

/**
 * Initiates SSO login by getting the SSO auth URL for the given email
 * @param {string} email - The user's email address
 * @returns {Promise<Object>} The response containing auth_url and state
 */
export const initiateSSOLogin = async (email) => {
  try {
    const response = await authApiClient.post('/api/v1/auth/sso/login', { email });
    return response.data;
  } catch (error) {
    console.error("Error in initiateSSOLogin:", error);
    throw error;
  }
};

/**
 * Creates or updates SSO organization
 * @param {Object} params - The SSO organization parameters
 * @param {string} params.orgName - Organization name
 * @param {Array<string>} params.domains - Array of domains
 * @param {string} params.itAdminEmail - IT admin email
* @returns {Promise<Object>} The SSO organization creation response
 */
export async function createSsoOrg({ orgName, domains, itAdminEmail }) {
  try {
    const response = await authApiClient.post('/api/v1/auth/sso/org', {
      orgName,
      domains,
      itAdminEmail,
    });
    return response.data;
  } catch (error) {
    console.error("Error in createSsoOrg:", error);
    throw error;
  }
}

/**
 * Gets SSO organization details for the current customer
 * @returns {Promise<Object>} The SSO organization details response
 */
export async function getSsoOrg() {
  try {
    const response = await authApiClient.get('/api/v1/auth/sso/org');
    return response.data;
  } catch (error) {
    console.error("Error in getSsoOrg:", error);
    throw error;
  }
}

/**
 * Deletes SSO organization
 * @param {string} customerId - Customer ID
 * @returns {Promise<Object>} The SSO organization deletion response
 */
export async function deleteSsoOrg(customerId) {
  try {
    const response = await apiClient.delete('/api/v1/auth/sso/org', {
      data: { customerId }
    });
    return response.data;
  } catch (error) {
    console.error("Error in deleteSsoOrg:", error);
    throw error;
  }
}

/**
 * Helper function to detect browser information
 * @returns {Object} Browser name and version information
 */
const getBrowserInfo = () => {
  const userAgent = navigator.userAgent;
  let browserName = 'Unknown';
  let browserVersion = 'Unknown';

  // Chrome
  if (userAgent.includes('Chrome') && !userAgent.includes('Edg')) {
    browserName = 'Chrome';
    const match = userAgent.match(/Chrome\/(\d+\.\d+)/);
    browserVersion = match ? match[1] : 'Unknown';
  }
  // Edge
  else if (userAgent.includes('Edg')) {
    browserName = 'Edge';
    const match = userAgent.match(/Edg\/(\d+\.\d+)/);
    browserVersion = match ? match[1] : 'Unknown';
  }
  // Firefox
  else if (userAgent.includes('Firefox')) {
    browserName = 'Firefox';
    const match = userAgent.match(/Firefox\/(\d+\.\d+)/);
    browserVersion = match ? match[1] : 'Unknown';
  }
  // Safari
  else if (userAgent.includes('Safari') && !userAgent.includes('Chrome')) {
    browserName = 'Safari';
    const match = userAgent.match(/Version\/(\d+\.\d+)/);
    browserVersion = match ? match[1] : 'Unknown';
  }

  return {
    name: browserName,
    version: browserVersion,
    fullUserAgent: userAgent
  };
};

/**
 * Submits user feedback with automatically gathered context information
 * @param {Object} params - The feedback parameters
 * @param {string} params.userMessage - The user's feedback message
 * @param {string} [params.userRole] - Optional user role (will be auto-detected if not provided)
 * @returns {Promise<Object>} The feedback submission response
 */
export const submitFeedback = async ({ userMessage, userRole }) => {
  try {
    // Get staff information from session storage
    const staffInfo = getCurrentStaff();
    if (!staffInfo) {
      throw new Error('User session not found. Please log in again.');
    }

    // Get customer ID from session storage
    const customerId = sessionStorage.getItem('customerId') || staffInfo.customerId;

    // Get current URL path
    const urlPath = window.location.pathname;

    // Gather browser information and format as string (max 1000 chars)
    const browser = getBrowserInfo();
    const screenRes = typeof window !== 'undefined' && window.screen ? `${window.screen.width}x${window.screen.height}` : 'Unknown';
    const viewportSize = `${window.innerWidth}x${window.innerHeight}`;
    const timezone = Intl.DateTimeFormat().resolvedOptions().timeZone;

    const browserInfoString = `Browser: ${browser.name} ${browser.version} | Platform: ${navigator.platform} | Screen: ${screenRes} | Viewport: ${viewportSize} | Language: ${navigator.language} | Timezone: ${timezone} | Online: ${navigator.onLine} | Cookies: ${navigator.cookieEnabled} | URL: ${window.location.href} | Referrer: ${document.referrer || 'None'} | Timestamp: ${new Date().toISOString()}`;

    // Ensure the string doesn't exceed 1000 characters
    const browserInfo = browserInfoString.length > 1000 ? browserInfoString.substring(0, 997) + '...' : browserInfoString;

    // Prepare the request body with automatically gathered data
    const requestBody = {
      userName: staffInfo.name || `${staffInfo.firstName || ''} ${staffInfo.lastName || ''}`.trim(),
      userRole: userRole || staffInfo.role || 'user', // Use provided role or fallback to staff role
      urlPath: urlPath,
      userMessage: userMessage,
      userEmail: staffInfo.email,
      userId: staffInfo.id || staffInfo._id,
      customerId: customerId,
      browserInfo: browserInfo
    };

    console.log('🚀 Submitting feedback:', requestBody);

    const response = await apiClient.post('/api/v1/feedback/send', requestBody);

    if (response.data.statusCode === 'SUCCESS') {
      console.log('✅ Feedback submitted successfully:', response.data);
      return response.data;
    }

    throw new Error(response.data.error || response.data.message || 'Failed to submit feedback');
  } catch (error) {
    console.error('❌ Error in submitFeedback:', error);

    // Enhanced error handling
    if (error.response) {
      console.error('Response data:', error.response.data);
      console.error('Response status:', error.response.status);

      // Return structured error response for better user experience
      if (error.response.data) {
        throw new Error(error.response.data.message || error.response.data.error || 'Failed to submit feedback');
      }
    } else if (error.request) {
      console.error('Request was made but no response received:', error.request);
      throw new Error('Network error: Unable to submit feedback. Please check your connection.');
    } else {
      console.error('Error setting up request:', error.message);
    }

    throw error;
  }
};

// ============================================
// DocuSeal API Functions
// ============================================

/**
 * Initializes a document submission for DocuSeal
 * @param {Object} params - The submission parameters
 * @param {string} params.formKey - The form key for the document
 * @returns {Promise<Object>} The submission response
 */
export const initializeDocuSealSubmission = async ({ formKey }) => {
  try {
    const response = await apiClient.post('/api/v1/docuseal/submission', {
      formKey
    });
    
    if (response.data.statusCode === "SUCCESS") {
      return {
        success: true,
        data: response.data.response
      };
    } else {
      return {
        success: false,
        error: response.data.message || 'Failed to initialize submission'
      };
    }
  } catch (error) {
    console.error('Error in initializeDocuSealSubmission:', error);
    return {
      success: false,
      error: error.message || 'Network error occurred',
      data: {
        embedSrc: "https://docuseal.com/d/LEVGR9rhZYf86M",
        submissionId: "demo-" + Date.now(),
        status: "pending"
      }
    };
  }
};

/**
 * Checks if document signing is completed
 * @param {Object} params - The completion check parameters
 * @param {string} params.formKey - The form key for the document
 * @returns {Promise<Object>} The completion status response
 */
export const checkDocuSealSigningComplete = async ({ formKey }) => {
  try {
    console.log('🔍 Calling DocuSeal signing-complete with formKey:', formKey);
    const response = await apiClient.post('/api/v1/docuseal/signing-complete', {
      formKey
    });
    
    if (response.data.statusCode === "SUCCESS") {
      return {
        success: true,
        data: response.data.response
      };
    } else {
      return {
        success: false,
        error: response.data.message || 'Failed to check signing status'
      };
    }
  } catch (error) {
    console.error('Error in checkDocuSealSigningComplete:', error);
    return {
      success: false,
      error: error.message || 'Network error occurred'
    };
  }
};

// ============================================
// Question Management API Functions
// ============================================

/**
 * Uploads a question set with file and/or typed questions
 * @param {FormData} formData - FormData containing branch, usecase, subusecase, questionSetName, and file
 * @returns {Promise<Object>} Upload response
 */
export const uploadCustomerQuestions = async (formData) => {
  try {
    const response = await apiClient.post('/api/v1/questions/upload-customer-questions', formData, {
      headers: {
        'Content-Type': 'multipart/form-data'
      }
    });
    return response.data;
  } catch (error) {
    console.error("Error in uploadCustomerQuestions:", error);
    throw error;
  }
};

/**
 * Fetches all question uploads with optional filters
 * @param {Object} params - Query parameters for filtering
 * @param {string} [params.branch] - Filter by branch
 * @param {string} [params.usecase] - Filter by usecase
 * @param {string} [params.subusecase] - Filter by subusecase
 * @param {string} [params.questionSetName] - Filter by question set name
 * @param {string} [params.status] - Filter by status (pending, processing, completed, failed)
 * @param {number} [params.skip] - Number of records to skip for pagination
 * @param {number} [params.limit] - Number of records to fetch
 * @returns {Promise<Object>} Question uploads response
 */
export const getCustomerQuestionUploads = async (params = {}) => {
  try {
    const response = await apiClient.get('/api/v1/questions/customer-question-uploads', { params });
    return response.data;
  } catch (error) {
    console.error("Error in getCustomerQuestionUploads:", error);
    throw error;
  }
};

/**
 * Gets a specific question upload by ID
 * @param {string} uploadId - The upload ID to fetch
 * @returns {Promise<Object>} Question upload details
 */
export const getCustomerQuestionUploadById = async (uploadId) => {
  try {
    const response = await apiClient.get(`/api/v1/questions/customer-question-uploads/${uploadId}`);
    return response.data;
  } catch (error) {
    console.error("Error in getCustomerQuestionUploadById:", error);
    throw error;
  }
};

/**
 * Gets standard questions for a specific path
 * @param {Object} data - Request body
 * @param {string} data.path - Path in format "branch-usecase-subusecase" (e.g., "medical-benefits_verification-default")
 * @param {string} data.type - Type of questions (e.g., "standard")
 * @returns {Promise<Object>} Standard questions response
 */
export const getStandardQuestions = async (data) => {
  try {
    const response = await apiClient.post('/api/v1/questions/get-questions', data);
    return response.data;
  } catch (error) {
    console.error("Error in getStandardQuestions:", error);
    throw error;
  }
};

// ==================== FAX AGENT API METHODS ====================

/**
 * Gets all faxes with optional filtering
 * @param {Object} params - Query parameters
 * @param {boolean} [params.include_inactive] - Include inactive faxes
 * @param {string} [params.direction] - Filter by direction
 * @param {string} [params.patient_name] - Filter by patient name
 * @param {string} [params.from_date] - Start date filter
 * @param {string} [params.to_date] - End date filter
 * @returns {Promise<Object>} The faxes response
 */
export const getFaxes = async (params = {}) => {
  try {
    console.log('🔍 Calling getFaxes with params:', params);
    const response = await apiClient.get('/api/v1/faxes', { params });

    if (response.data.statusCode === "SUCCESS") {
      return response.data.response || [];
    }

    throw new Error(response.data.message || 'Failed to fetch faxes');
  } catch (error) {
    console.error('Error in getFaxes:', error);
    throw new Error('Failed to fetch faxes. Please try again.');
  }
};

/**
 * Creates a new fax
 * @param {Object} faxData - Fax data to create
 * @returns {Promise<Object>} The creation response
 */
export const createFax = async (faxData) => {
  try {
    console.log('🔍 Calling createFax with data:', faxData);
    const response = await apiClient.post('/api/v1/faxes', faxData);

    if (response.data.statusCode === "SUCCESS") {
      return {
        success: true,
        message: response.data.message || 'Fax created successfully',
        data: response.data.data
      };
    }

    return {
      success: false,
      message: response.data.message || 'Failed to create fax'
    };
  } catch (error) {
    console.error('Error in createFax:', error);
    throw new Error('Failed to create fax. Please try again.');
  }
};

/**
 * Purchases a fax number
 * @param {Object} purchaseData - Purchase data including fax number and assignments
 * @returns {Promise<Object>} The purchase response
 */
export const purchaseFaxNumber = async (purchaseData) => {
  try {
    console.log('🔍 Calling purchaseFaxNumber with data:', purchaseData);
    const response = await apiClient.post('/api/v1/fax/ifax/purchase', purchaseData);

    if (response.data.statusCode === "SUCCESS" || response.status === 200) {
      return {
        success: true,
        message: response.data.message || 'Fax number purchased successfully',
        data: response.data.data || response.data.response
      };
    }

    // Handle FAILED statusCode with error field
    if (response.data.statusCode === "FAILED") {
      return {
        success: false,
        message: response.data.error || response.data.message || 'Failed to purchase fax number'
      };
    }

    return {
      success: false,
      message: response.data.message || response.data.error || 'Failed to purchase fax number'
    };
  } catch (error) {
    console.error('Error in purchaseFaxNumber:', error);
    // Check for error structure in response
    const errorMessage = error.response?.data?.error || error.response?.data?.message || 'Failed to purchase fax number. Please try again.';
    throw new Error(errorMessage);
  }
};

export const searchFaxNumber = async (data) => {
  try {
    console.log('🔍 Calling searchFaxNumber with data:', data);
    const response = await apiClient.post('/api/v1/fax/ifax/search', data);
    if (response.data.statusCode === 'SUCCESS') {
      return response.data.response;
    }
    // Handle FAILED statusCode with error field
    if (response.data.statusCode === 'FAILED') {
      throw new Error(response.data.error || response.data.message || 'Failed to search fax number');
    }
    throw new Error(response.data.message || response.data.error || 'Failed to search fax number');
  } catch (error) {
    console.error('Error in searchFaxNumber:', error);
    const errorMessage = error.response?.data?.error || error.response?.data?.message || error.message || 'Failed to search fax number. Please try again.';
    throw new Error(errorMessage);
  }
};

/**
 * Updates an existing fax
 * @param {string} faxId - ID of the fax to update
 * @param {Object} faxData - Updated fax data
 * @returns {Promise<Object>} The update response
 */
export const updateFax = async (faxId, faxData) => {
  try {
    console.log('🔍 Calling updateFax with ID:', faxId, 'and data:', faxData);
    const response = await apiClient.put(`/api/v1/faxes/${faxId}`, faxData);

    if (response.data.statusCode === "SUCCESS") {
      return {
        success: true,
        message: response.data.message || 'Fax updated successfully',
        data: response.data.data
      };
    }

    return {
      success: false,
      message: response.data.message || 'Failed to update fax'
    };
  } catch (error) {
    console.error('Error in updateFax:', error);
    throw new Error('Failed to update fax. Please try again.');
  }
};

/**
 * Deletes a fax
 * @param {string} faxId - ID of the fax to delete
 * @returns {Promise<Object>} The delete response
 */
export const deleteFax = async (faxId) => {
  try {
    console.log('🔍 Calling deleteFax with ID:', faxId);
    const response = await apiClient.delete(`/api/v1/faxes/${faxId}`);

    if (response.data.statusCode === "SUCCESS") {
      return {
        success: true,
        message: response.data.message || 'Fax deleted successfully'
      };
    }

    return {
      success: false,
      message: response.data.message || 'Failed to delete fax'
    };
  } catch (error) {
    console.error('Error in deleteFax:', error);
    throw new Error('Failed to delete fax. Please try again.');
  }
};

/**
 * Sends a fax with files using multipart/form-data.
 * This is the primary function for sending faxes with attachments.
 * @param {FormData} formData - The FormData object containing all fax data and files.
 * @returns {Promise<Object>} The raw API response from the server.
 */
export const sendFax = async (formData) => {
  try {
    console.log('📤 Calling sendFax with FormData...');
    
    const response = await apiClient.post('/api/v1/fax/send', formData, {
      headers: {
        'Content-Type': 'multipart/form-data'
      }
    });

    console.log('📡 Send Fax API Response received:', response.data);
    
    if (response.data.statusCode === "SUCCESS" || response.status === 200) {
      return response.data;
    }
    
    throw new Error(response.data.message || 'Failed to send fax');
  } catch (error) {
    console.error('❌ Error in sendFax:', error);
    throw new Error(error.response?.data?.message || 'Failed to send fax. Please try again.');
  }
};

/**
 * Creates a fax template
 * @param {Object} templateData - Template data
 * @returns {Promise<Object>} The template creation response
 */
export const createFaxTemplate = async (templateData) => {
  try {
    console.log('🔍 Calling createFaxTemplate with data:', templateData);
    const response = await apiClient.post('/api/v1/faxes/templates', templateData);

    if (response.data.statusCode === "SUCCESS") {
      return {
        success: true,
        message: response.data.message || 'Template created successfully',
        data: response.data.data
      };
    }

    return {
      success: false,
      message: response.data.message || 'Failed to create template'
    };
  } catch (error) {
    console.error('Error in createFaxTemplate:', error);
    throw new Error('Failed to create template. Please try again.');
  }
};

/**
 * Gets fax templates
 * @returns {Promise<Array>} Array of fax templates
 */
export const getFaxTemplates = async () => {
  try {
    console.log('🔍 Calling getFaxTemplates');
    const response = await apiClient.get('/api/v1/faxes/templates');

    if (response.data.statusCode === "SUCCESS") {
      return response.data.response || [];
    }

    throw new Error(response.data.message || 'Failed to fetch templates');
  } catch (error) {
    console.error('Error in getFaxTemplates:', error);
    throw new Error('Failed to fetch templates. Please try again.');
  }
};

/**
 * Gets a list of countries for fax numbers
 * @returns {Promise<Object>} The countries response
 */
export const getFaxCountries = async () => {
  try {
    const response = await apiClient.get('/api/v1/fax/ifax/countries');
    if (response.data.statusCode === 'SUCCESS') {
      return response.data.response;
    }
    throw new Error(response.data.message || 'Failed to fetch countries');
  } catch (error) {
    console.error('Error in getFaxCountries:', error);
    throw new Error('Failed to fetch countries. Please try again.');
  }
};

/**
 * Gets a list of area codes for a country
 * @returns {Promise<Object>} The area codes response
 */
export const getFaxAreaCodes = async () => {
  try {
    const response = await apiClient.post('/api/v1/fax/ifax/areas', { abbreviation: 'US' });
    if (response.data.statusCode === 'SUCCESS') {
      return response.data.response;
    }
    throw new Error(response.data.message || 'Failed to fetch area codes');
  } catch (error) {
    console.error('Error in getFaxAreaCodes:', error);
    throw new Error(error.response?.data?.message || error.message);
  }
};

/**
 * Gets a list of available fax numbers for an area
 * @param {string} areaId - The ID of the area
 * @returns {Promise<Object>} The numbers response
 */
export const getFaxNumbers = async (areaId) => {
  try {
    const response = await apiClient.post('/api/v1/fax/ifax/numbers', { id: areaId });
    if (response.data.statusCode === 'SUCCESS') {
      return response.data.response;
    }
    // Handle FAILED statusCode with error field
    if (response.data.statusCode === 'FAILED') {
      throw new Error(response.data.error || response.data.message || 'Failed to fetch numbers');
    }
    throw new Error(response.data.message || response.data.error || 'Failed to fetch numbers');
  } catch (error) {
    console.error('Error in getFaxNumbers:', error);
    const errorMessage = error.response?.data?.error || error.response?.data?.message || error.message || 'Failed to fetch numbers. Please try again.';
    throw new Error(errorMessage);
  }
};

// ============================================================================
// FAX DASHBOARD API CLIENT FUNCTIONS - RAW API CALLS ONLY
// ============================================================================

/**
 * Gets fax dashboard data - RAW API call only
 * @param {Object} params - Query parameters to pass directly to API
 * @returns {Promise<Object>} The raw API response
 */
export const getFaxDashboard = async (params = {}) => {
  try {
    console.log('🔍 Calling getFaxDashboard with params:', params);
    const response = await apiClient.get('/api/v1/fax/dashboard', { params });
    
    console.log('📡 API Response received:', response.data);
    
    if (response.data.statusCode === "SUCCESS") {
      return response.data.response || response.data;
    }
    
    throw new Error(response.data.message || 'Failed to fetch fax dashboard data');
  } catch (error) {
    console.error('❌ Error in getFaxDashboard:', error);
    throw new Error('Failed to fetch fax dashboard data. Please try again.');
  }
};

/**
 * Gets fax details by fax ID - RAW API call only
 * @param {string} faxId - The fax ID to fetch details for
 * @returns {Promise<Object>} The raw API response
 */
export const getFaxById = async (faxId) => {
  try {
    console.log('🔍 Calling getFaxById with faxId:', faxId);
    const response = await apiClient.get(`/api/v1/fax/detail/${faxId}`);
    
    console.log('📡 Fax Details API Response received:', response.data);
    
    if (response.data.statusCode === "SUCCESS") {
      return response.data.response || response.data;
    }
    
    throw new Error(response.data.message || 'Failed to fetch fax details');
  } catch (error) {
    console.error('❌ Error in getFaxById:', error);
    throw new Error('Failed to fetch fax details. Please try again.');
  }
};

/**
 * Sends a fax with files - RAW API call only
 * @param {Object} faxData - Fax data object
 * @param {File[]} files - Array of files to attach
 * @returns {Promise<Object>} The raw API response
 */
export const sendFaxWithFiles = async (faxData, files = []) => {
  try {
    console.log('📤 Calling sendFaxWithFiles with data:', faxData, 'files:', files.length);
    
    // Create FormData for multipart request
    const formData = new FormData();
    
    // Append fax data
    Object.keys(faxData).forEach(key => {
      if (faxData[key] !== undefined && faxData[key] !== null) {
        formData.append(key, faxData[key]);
      }
    });
    
    // Append files
    files.forEach((file, index) => {
      formData.append(`file`, file);
    });
    
    // Log FormData contents for debugging
    console.log('📋 FormData contents:');
    for (let [key, value] of formData.entries()) {
      console.log(`  ${key}:`, value instanceof File ? `File: ${value.name} (${value.size} bytes)` : value);
    }
    
    const response = await apiClient.post('/api/v1/fax/send', formData, {
      headers: {
        'Content-Type': 'multipart/form-data'
      }
    });
    
    console.log('📡 Send Fax API Response received:', response.data);
    
    if (response.data.statusCode === "SUCCESS") {
      return response.data.response || response.data;
    }
    
    throw new Error(response.data.message || 'Failed to send fax');
  } catch (error) {
    console.error('❌ Error in sendFaxWithFiles:', error);
    throw new Error('Failed to send fax. Please try again.');
  }
};

/**
 * Gets a list of purchased fax numbers
 * @returns {Promise<Object>} The purchased numbers response
 */
export const getPurchasedFaxNumbers = async () => {
  try {
    const response = await apiClient.get('/api/v1/fax/ifax/purchased');
    if (response.data.statusCode === 'SUCCESS') {
      return response.data.response;
    }
    throw new Error(response.data.message || 'Failed to fetch purchased numbers');
  } catch (error) {
    console.error('Error in getPurchasedFaxNumbers:', error);
    throw new Error('Failed to fetch purchased numbers. Please try again.');
  }
};

/**
 * Sends a fax with files using multipart/form-data.
 * This is the primary function for sending faxes with attachments.
 * @param {FormData} formData - The FormData object containing all fax data and files.
 * @returns {Promise<Object>} The raw API response from the server.
 */
export const sendFaxWithUpload = async (formData) => {
  try {
    console.log('📤 Calling sendFaxWithUpload with FormData...');
    
    const response = await apiClient.post('/api/v1/fax/send', formData, {
      headers: {
        'Content-Type': 'multipart/form-data'
      }
    });

    console.log('📡 Send Fax API Response received:', response.data);
    
    if (response.data.statusCode === "SUCCESS" || response.status === 200) {
      return response.data;
    }
    
    throw new Error(response.data.message || 'Failed to send fax');
  } catch (error) {
    console.error('❌ Error in sendFaxWithUpload:', error);
    throw new Error(error.response?.data?.message || 'Failed to send fax. Please try again.');
  }
};

/**
 * Get template structure (branch, usecase, subusecase hierarchy)
 * GET /api/v1/fax/templates/structure
 * 
 * @returns {Promise<Object>} Template structure with branches, usecases, and subusecases
 */
export const getFaxTemplateStructure = async () => {
  try {
    console.log('🔍 Calling getFaxTemplateStructure');
    const response = await apiClient.get('/api/v1/fax/templates/structure');

    console.log('✅ Template structure fetched:', response.data);
    
    if (response.data.statusCode === "SUCCESS") {
      return response.data;
    }
    
    throw new Error(response.data.message || 'Failed to fetch template structure');
  } catch (error) {
    console.error('❌ Error fetching template structure:', error);
    throw error;
  }
};

/**
 * Get signed URL for a template file
 * POST /api/v1/fax/templates/signed-url
 * 
 * @param {Object} templateData - Template file details
 * @param {string} templateData.branch - Branch name
 * @param {string} templateData.usecase - Usecase name
 * @param {string} templateData.subusecase - Subusecase name
 * @param {string} templateData.filename - Template filename
 * @param {number} [templateData.expiration_minutes=60] - URL expiration time in minutes
 * @returns {Promise<Object>} Response with signed URL
 */
export const getFaxTemplateSignedUrl = async (templateData) => {
  try {
    console.log('🔍 Calling getFaxTemplateSignedUrl with data:', templateData);
    
    const response = await apiClient.post('/api/v1/fax/templates/signed-url', {
      branch: templateData.branch,
      usecase: templateData.usecase,
      subusecase: templateData.subusecase,
      filename: templateData.filename,
      expiration_minutes: templateData.expiration_minutes || 60
    });

    console.log('✅ Template signed URL fetched:', response.data);
    
    if (response.data.statusCode === "SUCCESS") {
      return response.data;
    }
    
    throw new Error(response.data.message || 'Failed to fetch template signed URL');
  } catch (error) {
    console.error('❌ Error fetching template signed URL:', error);
    throw error;
  }
};

/**
 * Downloads a template file via backend proxy endpoint (bypasses CORS)
 * @param {Object} templateData - Template data with branch, usecase, subusecase, filename
 * @returns {Promise<Blob>} The downloaded template file as a blob
 */
export const downloadTemplateFile = async (templateData) => {
  try {
    console.log('🔍 Downloading template file via proxy:', templateData);
    
    // Get auth token and API key from session storage
    const token = sessionStorage.getItem(API_CONFIG.AUTH_TOKEN_KEY);
    const apiKey = sessionStorage.getItem("api_key");

    console.log('📋 Request headers:', { hasAuth: !!token, hasApiKey: !!apiKey });
    
    // Create a separate axios instance for file download with headers set upfront
    const downloadClient = axios.create({
      baseURL: API_CONFIG.BASE_URL,
      headers: {
        'Accept': 'application/pdf,application/octet-stream,*/*',
        'Cache-Control': 'no-cache, no-store, must-revalidate',
        'Pragma': 'no-cache',
        'Expires': '0',
        ...(token && { 'Authorization': `Bearer ${token}` }),
        ...(apiKey && { 'x-apikey': apiKey })
      }
    });
    
    const response = await downloadClient.get(
      `/api/v1/fax/templates/download/${encodeURIComponent(templateData.branch)}/${encodeURIComponent(templateData.usecase)}/${encodeURIComponent(templateData.subusecase)}/${encodeURIComponent(templateData.filename)}`,
      {
        responseType: 'blob' // Important for file downloads
      }
    );
    
    console.log('✅ Template file downloaded via proxy');
    return response.data; // Returns the blob
  } catch (error) {
    console.error('❌ Error downloading template file:', error);
    throw error;
  }
};

/**
 * Downloads a fax file via backend proxy endpoint (bypasses CORS)
 * @param {string} fileUrl - The file URL (GCS path or signed URL)
 * @returns {Promise<Blob>} The downloaded fax file as a blob
 */
export const downloadFaxFile = async (fileUrl) => {
  try {
    console.log('🔍 Downloading fax file via proxy:', fileUrl);
    
    // Get auth token and API key from session storage
    const token = sessionStorage.getItem(API_CONFIG.AUTH_TOKEN_KEY);
    const apiKey = sessionStorage.getItem("api_key");

    console.log('📋 Request headers:', { hasAuth: !!token, hasApiKey: !!apiKey });
    
    // Create a separate axios instance for file download with headers set upfront
    const downloadClient = axios.create({
      baseURL: API_CONFIG.BASE_URL,
      headers: {
        'Accept': 'application/pdf,application/octet-stream,*/*',
        'Cache-Control': 'no-cache, no-store, must-revalidate',
        'Pragma': 'no-cache',
        'Expires': '0',
        ...(token && { 'Authorization': `Bearer ${token}` }),
        ...(apiKey && { 'x-apikey': apiKey })
      }
    });
    
    // URL encode the fileUrl to handle signed URLs with query parameters
    const encodedFileUrl = encodeURIComponent(fileUrl);
    
    const response = await downloadClient.get(
      `/api/v1/fax/files/download?fileUrl=${encodedFileUrl}`,
      {
        responseType: 'blob' // Important for file downloads
      }
    );
    
    console.log('✅ Fax file downloaded via proxy');
    return response.data; // Returns the blob
  } catch (error) {
    console.error('❌ Error downloading fax file:', error);
    throw error;
  }
};

/**
 * Fetches the API docs manifest which lists all assets
 * GET /api/v2/docs/assets/docs-manifest.json
 * 
 * @returns {Promise<Object>} The manifest containing css, js, json, pages, external arrays
 */
export const getApiDocsManifest = async () => {
  try {
    console.log('📋 Fetching API docs manifest...');
    
    const response = await apiClient.get('/api/v2/docs/assets/docs-manifest.json');
    
    console.log('✅ Manifest fetched:', response.data);
    
    return response.data;
  } catch (error) {
    console.error('❌ Error fetching API docs manifest:', error);
    throw error;
  }
};

/**
 * Fetches API documentation HTML from the backend
 * GET /api/v2/docs/{page}
 * 
 * @param {string} page - The docs page to fetch (default: 'index')
 * @returns {Promise<string>} The API documentation HTML content
 */
export const getApiDocsHtml = async (page = 'index') => {
  try {
    console.log(`📚 Fetching API documentation page: ${page}...`);
    
    const response = await apiClient.get(`/api/v2/docs/${page}`, {
      responseType: 'text'
    });

    console.log('✅ API documentation fetched, HTML length:', response.data.length);
    
    return response.data;
  } catch (error) {
    console.error('❌ Error fetching API documentation:', error);
    throw error;
  }
};

/**
 * Fetches API documentation asset (CSS/JS/JSON) from the backend
 * GET /api/v2/docs/assets/{path}
 * 
 * @param {string} assetPath - The asset path (e.g., 'css/custom.css')
 * @returns {Promise<string>} The asset content
 */
export const getApiDocsAsset = async (assetPath) => {
  try {
    console.log(`📦 Fetching API docs asset: ${assetPath}...`);
    
    const response = await apiClient.get(`/api/v2/docs/assets/${assetPath}`, {
      responseType: 'text'
    });

    console.log(`✅ Asset fetched: ${assetPath}, length: ${response.data.length}`);
    
    return response.data;
  } catch (error) {
    console.error(`❌ Error fetching asset ${assetPath}:`, error);
    throw error;
  }
};

/**
 * Fetches an external asset (no auth required)
 * 
 * @param {string} url - The external URL
 * @returns {Promise<string>} The asset content
 */
export const getExternalAsset = async (url) => {
  try {
    console.log(`🌐 Fetching external asset: ${url}...`);
    
    const response = await fetch(url);
    if (!response.ok) {
      throw new Error(`Failed to fetch ${url}: ${response.status}`);
    }
    const content = await response.text();
    
    console.log(`✅ External asset fetched: ${url}, length: ${content.length}`);
    
    return content;
  } catch (error) {
    console.error(`❌ Error fetching external asset ${url}:`, error);
    throw error;
  }
};

// Cache for API documentation assets to speed up navigation
let apiDocsCache = {
  manifest: null,
  cssAssets: null,
  jsAssets: null,
  externalAssets: null,
  jsonAssets: null,
  lastCacheTime: null,
  cacheExpiry: 5 * 60 * 1000 // 5 minutes cache expiry
};

/**
 * Clears the API docs cache (useful for debugging or forced refresh)
 */
export const clearApiDocsCache = () => {
  apiDocsCache = {
    manifest: null,
    cssAssets: null,
    jsAssets: null,
    externalAssets: null,
    jsonAssets: null,
    lastCacheTime: null,
    cacheExpiry: 5 * 60 * 1000
  };
};

/**
 * Checks if cache is valid (not expired)
 */
const isCacheValid = () => {
  if (!apiDocsCache.lastCacheTime) return false;
  const now = Date.now();
  return (now - apiDocsCache.lastCacheTime) < apiDocsCache.cacheExpiry;
};

/**
 * Fetches the complete API documentation with all assets inlined
 * Uses caching to optimize navigation between pages
 * 
 * @param {string} page - The docs page to fetch (default: 'index')
 * @param {boolean} forceRefresh - Force refresh of cached assets (default: false)
 * @returns {Promise<string>} Complete HTML with inlined assets
 */
export const getApiDocsComplete = async (page = 'index', forceRefresh = false) => {
  // Import Redoc helpers
  const {
    detectRedocElement,
    replaceRedocElement,
    injectRedocContainer,
    findOpenApiSpec,
    injectRedocAssets,
    fixNavigationLinks,
    injectAuthToken,
    ensureNoRedocElements,
    inlineAssets,
    injectFallbackAssets
  } = await import('./redocHelper');
  
  try {
    console.log(`📚 Fetching complete API documentation for page: ${page}...`);
    
    // Step 1: Get or fetch manifest (use cache if valid)
    let manifest;
    if (!forceRefresh && isCacheValid() && apiDocsCache.manifest) {
      manifest = apiDocsCache.manifest;
      console.log('📦 Using cached manifest');
    } else {
      manifest = await getApiDocsManifest();
      apiDocsCache.manifest = manifest;
    }
    
    // Step 2: Get or fetch assets (use cache if valid)
    let successfulCssAssets, successfulJsAssets, externalAssets, successfulJsonAssets;
    
    if (!forceRefresh && isCacheValid() && 
        apiDocsCache.cssAssets && apiDocsCache.jsAssets && 
        apiDocsCache.externalAssets && apiDocsCache.jsonAssets) {
      // Use cached assets
      successfulCssAssets = apiDocsCache.cssAssets;
      successfulJsAssets = apiDocsCache.jsAssets;
      externalAssets = apiDocsCache.externalAssets;
      successfulJsonAssets = apiDocsCache.jsonAssets;
      console.log('📦 Using cached assets (CSS, JS, external, JSON)');
    } else {
      // Fetch all assets in parallel, handling missing assets gracefully
      const fetchAssetSafely = async (getAssetFn, file) => {
        try {
          const content = await getAssetFn(file);
          return { file, content, success: true };
        } catch (error) {
          return { file, content: null, success: false };
        }
      };

      const [cssAssets, jsAssets, externalAssetsArray, jsonAssets] = await Promise.all([
        Promise.all((manifest.css || []).map(async (file) => fetchAssetSafely(getApiDocsAsset, file))),
        Promise.all((manifest.js || []).map(async (file) => fetchAssetSafely(getApiDocsAsset, file))),
        Promise.all((manifest.external || []).map(async (url) => fetchAssetSafely(getExternalAsset, url))),
        Promise.all((manifest.json || []).map(async (file) => fetchAssetSafely(getApiDocsAsset, file)))
      ]);

      // Filter out failed assets
      successfulCssAssets = cssAssets.filter(asset => asset.success);
      successfulJsAssets = jsAssets.filter(asset => asset.success);
      externalAssets = externalAssetsArray.filter(asset => asset.success);
      successfulJsonAssets = jsonAssets.filter(asset => asset.success);
      
      // Cache the assets
      apiDocsCache.cssAssets = successfulCssAssets;
      apiDocsCache.jsAssets = successfulJsAssets;
      apiDocsCache.externalAssets = externalAssets;
      apiDocsCache.jsonAssets = successfulJsonAssets;
      apiDocsCache.lastCacheTime = Date.now();
      
      console.log('✅ Assets fetched and cached:', { 
        css: `${successfulCssAssets.length}/${cssAssets.length}`, 
        js: `${successfulJsAssets.length}/${jsAssets.length}`, 
        external: externalAssets.length, 
        json: `${successfulJsonAssets.length}/${jsonAssets.length}` 
      });
    }
    
    // Step 3: Fetch the HTML page (this changes per page, so always fetch)
    let html = await getApiDocsHtml(page);
    console.log(`📄 Fetched HTML for page: ${page}, length: ${html.length}`);
    
    // Step 4: Detect and handle <redoc> element
    const { hasRedocTag, specUrl } = detectRedocElement(html);
    let hasRedocElement = false;
    let openApiSpec = null;
    
    // For api-reference page, always try to find and inline the spec
    const isApiReferencePage = page === 'api-reference' || page.includes('api-reference');
    
    if (specUrl || hasRedocTag) {
      // Find the OpenAPI spec
      const specAsset = findOpenApiSpec(successfulJsonAssets, specUrl);
      if (specAsset) {
        hasRedocElement = true;
        openApiSpec = specAsset.content;
        // Replace <redoc> element with container div
        html = replaceRedocElement(html);
      }
    } else if (isApiReferencePage) {
      // Fallback for api-reference page without <redoc> element
      console.log('⚠️ api-reference page should have Redoc but no <redoc> found');
      const specAsset = findOpenApiSpec(successfulJsonAssets);
      if (specAsset) {
        hasRedocElement = true;
        openApiSpec = specAsset.content;
        html = injectRedocContainer(html);
        console.log('✅ Injected redoc-container div manually');
      } else {
        // If spec not found in manifest assets, try to fetch it directly
        console.log('⚠️ OpenAPI spec not found in manifest, trying to fetch directly...');
        try {
          const directSpec = await getApiDocsAsset('v2_openapi.json');
          if (directSpec) {
            hasRedocElement = true;
            openApiSpec = directSpec;
            html = injectRedocContainer(html);
            console.log('✅ Fetched OpenAPI spec directly and injected container');
          }
        } catch (specError) {
          console.error('❌ Failed to fetch OpenAPI spec directly:', specError);
        }
      }
    }
    
    // Step 5: Parse HTML and inline assets
    const parser = new DOMParser();
    const doc = parser.parseFromString(html, 'text/html');
    
    // Step 5.5: Remove any existing Redoc.init scripts that use URLs (they will be replaced with inline version)
    const existingRedocScripts = doc.querySelectorAll('script');
    existingRedocScripts.forEach(script => {
      const scriptContent = script.textContent || script.innerHTML;
      // Check if this script contains Redoc.init with a URL
      if (scriptContent.includes('Redoc.init') && (scriptContent.includes('http://') || scriptContent.includes('https://') || scriptContent.includes('/api/v2/docs/assets/'))) {
        console.log('🗑️ Removing URL-based Redoc.init script, will be replaced with inline version');
        script.remove();
      }
    });
    
    // Step 6: Inline CSS and JS assets (only successful ones)
    inlineAssets(doc, successfulCssAssets, successfulJsAssets, externalAssets);
    
    // Step 6.5: Inject fallback CSS/JS if assets are missing
    injectFallbackAssets(doc, successfulCssAssets, successfulJsAssets);
    
    // Step 7: Ensure Redoc container exists for api-reference page
    if (isApiReferencePage && !doc.getElementById('redoc-container')) {
      console.log('⚠️ api-reference page but no redoc-container found, creating one...');
      const container = doc.createElement('div');
      container.id = 'redoc-container';
      container.style.width = '100%';
      const body = doc.body || doc.getElementsByTagName('body')[0];
      if (body) {
        body.appendChild(container);
        console.log('✅ Created redoc-container div');
      }
    }
    
    // Step 8: If we have a Redoc page, inject Redoc assets
    if (hasRedocElement && openApiSpec) {
      injectRedocAssets(doc, openApiSpec);
    } else if (isApiReferencePage && !hasRedocElement) {
      // For api-reference page, if we still don't have the spec, try one more time
      console.log('⚠️ api-reference page but no spec found, attempting final fetch...');
      try {
        const finalSpec = await getApiDocsAsset('v2_openapi.json');
        if (finalSpec) {
          openApiSpec = finalSpec;
          // Ensure container exists
          if (!doc.getElementById('redoc-container')) {
            const container = doc.createElement('div');
            container.id = 'redoc-container';
            container.style.width = '100%';
            const body = doc.body || doc.getElementsByTagName('body')[0];
            if (body) {
              body.appendChild(container);
            }
          }
          injectRedocAssets(doc, openApiSpec);
          hasRedocElement = true;
          console.log('✅ Successfully injected Redoc assets with final fetch');
        } else {
          console.error('❌ Final spec fetch returned empty/null');
        }
      } catch (finalError) {
        console.error('❌ Final attempt to fetch spec failed:', finalError);
      }
    }
    
    // Step 9: Fix navigation links
    fixNavigationLinks(doc);
    
    // Step 10: Inject auth token
    const token = sessionStorage.getItem('access_token');
    injectAuthToken(doc, token);
    
    // Step 11: Serialize back to HTML string
    let completeHtml = '<!DOCTYPE html>\n' + doc.documentElement.outerHTML;
    
    // Final safety check
    completeHtml = ensureNoRedocElements(completeHtml);
    
    // Final validation for api-reference page
    if (isApiReferencePage) {
      const hasContainer = completeHtml.includes('id="redoc-container"');
      const hasSpecScript = completeHtml.includes('id="openapi-spec-data"');
      const hasInitScript = completeHtml.includes('initRedocWithSpec');
      console.log('📊 Final validation for api-reference:', {
        hasContainer,
        hasSpecScript,
        hasInitScript,
        specSize: openApiSpec ? openApiSpec.length : 0
      });
      
      if (!hasContainer) {
        console.error('❌ WARNING: redoc-container not found in final HTML!');
      }
      if (!hasSpecScript && hasRedocElement) {
        console.error('❌ WARNING: openapi-spec-data script not found in final HTML!');
      }
      if (!hasInitScript && hasRedocElement) {
        console.error('❌ WARNING: Redoc init script not found in final HTML!');
      }
    }
    
    console.log('✅ Complete API documentation prepared with all inlined assets');
    console.log(`📊 Summary: hasRedoc=${hasRedocElement}, specSize=${openApiSpec ? openApiSpec.length : 0}`);
    
    return completeHtml;
  } catch (error) {
    console.error('❌ Error preparing complete API documentation:', error);
    throw error;
  }
};